import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { collection, doc, getDoc, getDocs, query, serverTimestamp, setDoc, where } from 'firebase/firestore'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowLeft,
  faEnvelope,
  faGlobe,
  faMapMarkerAlt,
  faMessage,
  faPhone
} from '@fortawesome/free-solid-svg-icons'
import coverPhotoContImage from '../Images/default-cover.gif'
import profilePictureContImage from '../Images/defprofile.png'
import { db } from '../firebase'

const normalizeText = (value) => String(value || '').trim()

const normalizeKey = (value) => normalizeText(value)
  .toLowerCase()
  .replace(/&/g, 'and')
  .replace(/[^a-z0-9]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()

const normalizeTimestamp = (value) => {
  if (!value) return 0
  if (typeof value.toMillis === 'function') return value.toMillis()
  if (typeof value.seconds === 'number') return value.seconds * 1000
  return new Date(value).getTime() || 0
}

const normalizeList = (value, limit = Infinity) => {
  if (!value) return []
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') return item.trim()
        return normalizeText(item?.text || item?.title || item?.description || item?.body)
      })
      .filter(Boolean)
      .slice(0, limit)
  }

  if (typeof value === 'string') {
    return value
      .split(/\r?\n|•|;/)
      .map((item) => item.trim())
      .filter(Boolean)
      .slice(0, limit)
  }

  return []
}

const normalizeDiagnoses = (items = [], limit = 5) => (
  (Array.isArray(items) ? items : [])
    .map((item) => ({
      topic: normalizeText(item?.topic),
      subtopic: normalizeText(item?.subtopic),
      misconception: normalizeText(item?.misconception),
      evidence: normalizeText(item?.evidence),
      bestIntervention: normalizeText(item?.bestIntervention || item?.best_intervention)
    }))
    .filter((item) => item.topic || item.subtopic || item.misconception)
    .slice(0, limit)
)

const chunkArray = (items, size) => {
  const chunks = []
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size))
  }
  return chunks
}

const buildFireflyReadId = (instructorId, studentId, reportId) => (
  `${instructorId}_${studentId}_${reportId}`.replace(/[^a-zA-Z0-9_-]/g, '_')
)

const formatFireflyDate = (value) => {
  const timestamp = Number(value) || 0
  if (!timestamp) return 'Session date not captured'

  return new Intl.DateTimeFormat('en-KE', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(new Date(timestamp))
}

const formatFireflyShortDate = (value) => {
  const timestamp = Number(value) || 0
  if (!timestamp) return 'No date'

  return new Intl.DateTimeFormat('en-KE', {
    month: 'short',
    day: 'numeric'
  }).format(new Date(timestamp))
}

const formatFireflyDuration = (value) => {
  const totalSeconds = Math.max(0, Number(value) || 0)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  if (minutes <= 0) return `${seconds}s`
  if (seconds === 0) return `${minutes} min`
  return `${minutes} min ${seconds}s`
}

const formatCalendarDate = (value) => {
  if (!value) return 'Not scheduled'
  const timestamp = normalizeTimestamp(value)
  const date = timestamp ? new Date(timestamp) : new Date(value)
  if (Number.isNaN(date.getTime())) return 'Not scheduled'
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

const getTopCounts = (items = [], limit = 6) => {
  const counts = items.reduce((acc, item) => {
    const label = normalizeText(item)
    if (!label) return acc
    acc[label] = (acc[label] || 0) + 1
    return acc
  }, {})

  return Object.entries(counts)
    .map(([label, value]) => ({ label, value }))
    .sort((first, second) => second.value - first.value || first.label.localeCompare(second.label))
    .slice(0, limit)
}

const getSignalScore = (value) => {
  const signal = normalizeKey(value)
  if (!signal) return 50
  if (signal.includes('high') || signal.includes('confident') || signal.includes('strong')) return 88
  if (signal.includes('improv') || signal.includes('rising') || signal.includes('better')) return 74
  if (signal.includes('steady') || signal.includes('stable')) return 58
  if (signal.includes('low') || signal.includes('drop') || signal.includes('uncertain')) return 34
  return 50
}

const getProfileFitScore = (value) => {
  const numericValue = Number(value)
  if (!Number.isNaN(numericValue) && numericValue > 0) {
    return Math.max(0, Math.min(100, numericValue))
  }

  const signal = normalizeKey(value)
  if (!signal) return 50
  if (signal.includes('high') || signal.includes('strong') || signal.includes('best')) return 88
  if (signal.includes('medium') || signal.includes('moderate') || signal.includes('helpful')) return 64
  if (signal.includes('low') || signal.includes('limited') || signal.includes('weak')) return 36
  return 50
}

const getProfileBullets = (items = [], limit = 4) => (
  (Array.isArray(items) ? items : [])
    .map((item) => {
      if (typeof item === 'string') {
        return { label: item, evidence: '', action: '' }
      }

      return {
        label: normalizeText(item?.label || item?.title || item?.theme || item?.mode),
        evidence: normalizeText(item?.evidence || item?.reason || item?.description || item?.whyItMatters),
        action: normalizeText(item?.action || item?.nextStep || item?.guidance || item?.firstMove)
      }
    })
    .filter((item) => item.label || item.evidence || item.action)
    .slice(0, limit)
)

const normalizeAssessmentList = (items = []) => (
  (Array.isArray(items) ? items : []).filter(Boolean)
)

const renderAssessmentScore = (value, outOf = 5) => {
  const score = Number(value)
  if (!Number.isFinite(score)) return 'Not scored'
  return `${score}/${outOf}`
}

const getAssessmentScorePercent = (value, outOf = 5) => {
  const score = Number(value)
  const total = Number(outOf)
  if (!Number.isFinite(score) || !Number.isFinite(total) || total <= 0) return 0
  return Math.max(0, Math.min(100, (score / total) * 100))
}

const getAssessmentChartItems = (items = [], limit = 6) => (
  normalizeAssessmentList(items)
    .map((item, index) => {
      const label = normalizeText(item?.label || item?.title || item?.promptLabel || `Item ${index + 1}`)
      const score = Number(item?.score)
      const outOf = Number(item?.outOf || 5)

      if (!Number.isFinite(score) || !Number.isFinite(outOf) || outOf <= 0) return null

      return {
        id: item?.id || label || `chart-${index}`,
        label,
        shortLabel: label.length > 18 ? `${label.slice(0, 18).trim()}...` : label,
        score,
        outOf,
        percent: getAssessmentScorePercent(score, outOf)
      }
    })
    .filter(Boolean)
    .slice(0, limit)
)

const formatProfileDate = (value) => {
  const timestamp = normalizeTimestamp(value) || Number(value) || 0
  if (!timestamp) return 'Not available'

  return new Intl.DateTimeFormat('en-KE', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(new Date(timestamp))
}

const getSubmissionStatus = (submission = {}) => (
  normalizeKey(submission.status || submission.reviewStatus || 'submitted')
)

const sanitizeRichTextHtml = (input) => {
  if (!input) return ''
  const parser = new DOMParser()
  const doc = parser.parseFromString(input, 'text/html')
  const allowedTags = new Set(['b', 'strong', 'i', 'em', 'u', 's', 'code', 'pre', 'br', 'p', 'div', 'ul', 'ol', 'li', 'blockquote', 'span'])

  const walk = (node) => {
    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const tag = child.tagName.toLowerCase()
        if (!allowedTags.has(tag)) {
          const fragment = doc.createDocumentFragment()
          while (child.firstChild) fragment.appendChild(child.firstChild)
          child.replaceWith(fragment)
          return
        }
        Array.from(child.attributes).forEach((attr) => child.removeAttribute(attr.name))
        walk(child)
      } else if (child.nodeType === Node.COMMENT_NODE) {
        child.remove()
      }
    })
  }

  walk(doc.body)
  return doc.body.innerHTML
}

const getAssignmentTypeLabel = (assignment = {}) => (
  assignment.assignmentTypeLabel
  || assignment.assignmentType
  || 'Task'
)

const getAssignmentWeekLabel = (assignment = {}) => (
  normalizeText(assignment.assignmentWeekLabel)
  || (normalizeText(assignment.assignmentWeek) ? `Week ${normalizeText(assignment.assignmentWeek)}` : '')
)

const getSubmissionLinks = (submission = {}) => (
  [
    ['Repository', submission.githubLink || submission.githubUrl || submission.repositoryUrl || submission.repoLink],
    ['Project', submission.projectLink || submission.projectUrl || submission.liveLink || submission.liveUrl],
    ['Video', submission.videoLink || submission.videoUrl || submission.demoVideoLink || submission.demoUrl],
    ['Attachment', submission.fileLink || submission.attachmentUrl || submission.documentUrl],
    ['Submission', submission.submissionLink || submission.link || submission.resourceLink]
  ].filter(([, value]) => normalizeText(value))
)

const getSubmissionResponseItems = (submission = {}) => (
  Array.isArray(submission.responseItems)
    ? submission.responseItems.map((item) => normalizeText(item)).filter(Boolean)
    : []
)

const getSubmissionResponseHtmlItems = (submission = {}) => (
  Array.isArray(submission.responseItemsHtml)
    ? submission.responseItemsHtml.map((item) => sanitizeRichTextHtml(item)).filter(Boolean)
    : []
)

const FireflyLineChart = ({ reports = [] }) => {
  const recentReports = [...reports]
    .sort((first, second) => (first.startedAtMs || 0) - (second.startedAtMs || 0))
    .slice(-8)
  const values = recentReports.length
    ? recentReports.map((report) => getSignalScore(report.confidenceTrend))
    : [0]
  const chartLeft = 36
  const chartRight = 296
  const chartTop = 26
  const chartBottom = 122
  const chartHeight = chartBottom - chartTop
  const yTicks = [0, 25, 50, 75, 100]
  const points = values.map((value, index) => {
    const x = values.length === 1 ? ((chartLeft + chartRight) / 2) : chartLeft + (index * ((chartRight - chartLeft) / Math.max(values.length - 1, 1)))
    const y = chartBottom - ((value / 100) * chartHeight)
    return { x, y, value, label: formatFireflyShortDate(recentReports[index]?.endedAtMs || recentReports[index]?.startedAtMs) }
  })
  const polylinePoints = points.length ? points.map((point) => `${point.x},${point.y}`).join(' ') : `${chartLeft},${chartBottom}`

  return (
    <div className='instructorFireflyChart instructorFireflyLineChart'>
      <svg viewBox='0 0 320 170' role='img' aria-label='Confidence trend chart'>
        {yTicks.map((tick) => {
          const y = chartBottom - ((tick / 100) * chartHeight)
          return (
            <g key={`tick-${tick}`}>
              <line className='instructorFireflyLineGrid' x1={chartLeft} y1={y} x2={chartRight} y2={y} />
              <text x={chartLeft - 8} y={y + 4} textAnchor='end'>{tick}</text>
            </g>
          )
        })}
        <line className='instructorFireflyLineAxis' x1={chartLeft} y1={chartBottom} x2={chartRight} y2={chartBottom} />
        <line className='instructorFireflyLineAxis' x1={chartLeft} y1={chartTop} x2={chartLeft} y2={chartBottom} />
        <polyline points={polylinePoints} />
        {points.map((point, index) => (
          <g key={`${point.value}-${index}`}>
            <circle cx={point.x} cy={point.y} r='4' />
            <text x={point.x} y='148' textAnchor='middle'>{point.label}</text>
          </g>
        ))}
        <text x='166' y='165' textAnchor='middle'>Sessions</text>
        <text x='14' y='76' textAnchor='middle' transform='rotate(-90 14 76)'>Confidence</text>
      </svg>
    </div>
  )
}

const FireflyBarChart = ({ items = [], emptyLabel = 'No data yet' }) => {
  const maxValue = Math.max(...items.map((item) => item.value), 1)

  return (
    <div className='instructorFireflyBars'>
      {(items.length ? items : [{ label: emptyLabel, value: 0 }]).map((item) => (
        <div key={item.label}>
          <span>{item.label}</span>
          <strong>{item.value}</strong>
          <i style={{ width: `${Math.max(6, (item.value / maxValue) * 100)}%` }} />
        </div>
      ))}
    </div>
  )
}

const FireflyBalanceChart = ({ strengths = [], struggles = [] }) => {
  const total = Math.max(strengths.length + struggles.length, 1)
  const strengthShare = Math.round((strengths.length / total) * 100)

  return (
    <div className='instructorFireflyBalance'>
      <div style={{ '--strength-share': `${strengthShare}%` }} />
      <section>
        <span><b /> Strengths {strengths.length}</span>
        <span><b /> Gaps {struggles.length}</span>
      </section>
    </div>
  )
}

const LEARNING_PROFILE_API_BASE = (
  process.env.REACT_APP_LEARNING_PROFILE_API_URL
  || 'https://us-central1-ubunifu-college.cloudfunctions.net/learningProfilesApi'
).replace(/\/$/, '')

function StudentProfileComp() {
  const auth = getAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { studentId } = useParams()
  const previewStudent = location.state?.searchStudentPreview || {}

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [instructorId, setInstructorId] = useState('')
  const [student, setStudent] = useState({
    name: previewStudent.name || 'Student',
    email: previewStudent.email || '',
    profilePic: previewStudent.avatar || profilePictureContImage,
    coverPhoto: coverPhotoContImage
  })
  const [studentClasses, setStudentClasses] = useState([])
  const [fireflyProfile, setFireflyProfile] = useState(null)
  const [assignmentLearningProfile, setAssignmentLearningProfile] = useState(null)
  const [fireflyLearningProfile, setFireflyLearningProfile] = useState(null)
  const [fireflyReports, setFireflyReports] = useState([])
  const [fireflyReadMap, setFireflyReadMap] = useState({})
  const [assignments, setAssignments] = useState([])
  const [assignmentAssessments, setAssignmentAssessments] = useState({})
  const [refreshKey, setRefreshKey] = useState(0)
  const [refreshingProfile, setRefreshingProfile] = useState(false)
  const [refreshMessage, setRefreshMessage] = useState('')
  const [activeTab, setActiveTab] = useState('overview')
  const [assignmentView, setAssignmentView] = useState('general')
  const [selectedAssignmentReviewId, setSelectedAssignmentReviewId] = useState('')
  const [fireflyView, setFireflyView] = useState('general')
  const [selectedReportId, setSelectedReportId] = useState('')
  const openWorkspaceMessage = useCallback(() => {
    if (!studentId) return
    navigate('/workspace', {
      state: {
        openDMUserId: studentId,
        openDMName: student.name || 'Student',
        openDMAvatar: student.profilePic || profilePictureContImage,
        openDMCourse: student.course || ''
      }
    })
  }, [navigate, studentId, student.name, student.profilePic, student.course])

  const handleRefreshLearnerProfile = useCallback(async () => {
    const currentUser = auth.currentUser
    if (!currentUser || !studentId) {
      setRefreshMessage('Please sign in again before rebuilding this learner profile.')
      return
    }

    setRefreshingProfile(true)
    setRefreshMessage('')

    try {
      const idToken = await currentUser.getIdToken(true)
      const response = await fetch(`${LEARNING_PROFILE_API_BASE}/profiles/rebuild`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({
          studentId,
          source: 'all',
          force: true
        })
      })

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}))
        throw new Error(payload?.error || 'The learner profile could not be rebuilt right now.')
      }

      setRefreshMessage('Learner profile rebuilt. Loading the latest saved data...')
      setRefreshKey((current) => current + 1)
    } catch (refreshError) {
      console.error('Error rebuilding learner profile:', refreshError)
      setRefreshMessage(refreshError?.message || 'The learner profile could not be rebuilt right now.')
    } finally {
      setRefreshingProfile(false)
    }
  }, [auth, studentId])

  useEffect(() => {
    let isMounted = true

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        if (!isMounted) return
        setLoading(false)
        setError('Please sign in to open this learner profile.')
        return
      }

      if (!studentId) {
        setLoading(false)
        setError('A student record was not provided.')
        return
      }

      setLoading(true)
      setError('')
      setInstructorId(user.uid)

      try {
        const classesSnapshot = await getDocs(
          query(collection(db, 'allclasses'), where('instructor', '==', user.uid))
        )

        const classList = classesSnapshot.docs.map((classDoc) => ({ id: classDoc.id, ...classDoc.data() }))
        const classIds = classList.map((classItem) => classItem.roomID || classItem.id).filter(Boolean)
        const classMap = classList.reduce((acc, classItem) => {
          acc[classItem.roomID || classItem.id] = classItem
          return acc
        }, {})

        const membershipSnapshots = await Promise.all(
          classIds.map((classId) => (
            getDocs(query(collection(db, 'StudentsInClass'), where('classID', '==', classId)))
          ))
        )

        const memberships = membershipSnapshots.flatMap((snapshot) => (
          snapshot.docs.map((studentDoc) => ({ id: studentDoc.id, ...studentDoc.data() }))
        ))

        const studentMemberships = memberships.filter((item) => (
          (item.studentID || item.userId || item.id) === studentId
        ))

        if (!studentMemberships.length) {
          throw new Error('This student is not connected to one of your classes.')
        }

        const [profileSnapshot, learnerSnapshot, sessionsSnapshot, readsSnapshot, assignmentsSnapshot, assignmentProfileSnapshot, fireflyProfileSnapshot] = await Promise.all([
          getDocs(query(collection(db, 'users'), where('userId', '==', studentId))),
          getDoc(doc(db, 'plm', studentId)),
          getDocs(collection(db, 'plm', studentId, 'sessions')),
          getDocs(query(collection(db, 'fireflyReportReads'), where('instructorId', '==', user.uid))),
          getDocs(query(collection(db, 'courseAssignments'), where('instructorId', '==', user.uid))),
          getDoc(doc(db, 'studentAssignmentLearningProfiles', studentId)),
          getDoc(doc(db, 'studentFireflyLearningProfiles', studentId))
        ])

        const profileDoc = profileSnapshot.docs[0]
        const profileData = profileDoc?.data() || {}
        const primaryMembership = studentMemberships[0] || {}
        const classRecords = studentMemberships
          .map((membership) => {
            const connectedClass = classMap[membership.classID] || {}
            return {
              id: membership.classID || connectedClass.roomID || connectedClass.id || membership.id,
              name: connectedClass.classDesc || membership.className || membership.course || 'Class',
              course: membership.course || profileData.course || connectedClass.courseName || '',
              cohortDate: membership.cohortDate || connectedClass.intakeDate || '',
              startDate: connectedClass.startDate || '',
              startTime: connectedClass.startTime || '',
              endTime: connectedClass.endTime || '',
              roomID: connectedClass.roomID || connectedClass.id || '',
              schedule: connectedClass.classDays || connectedClass.days || connectedClass.schedule || ''
            }
          })
          .filter((classItem, index, list) => (
            list.findIndex((candidate) => candidate.id === classItem.id) === index
          ))

        const reportList = sessionsSnapshot.docs
          .map((sessionDoc) => {
            const data = sessionDoc.data() || {}
            return {
              id: sessionDoc.id,
              status: normalizeText(data.status),
              summary: normalizeText(data.summary),
              sessionFocus: normalizeText(data.sessionFocus),
              startedAtMs: Number(data.startedAtMs) || 0,
              endedAtMs: Number(data.endedAtMs) || 0,
              durationSeconds: Number(data.durationSeconds) || 0,
              topicsCovered: normalizeList(data.topicsCovered, 8),
              strengths: normalizeList(data.strengths, 5),
              struggles: normalizeList(data.struggles, 5),
              misconceptions: normalizeList(data.misconceptions, 5),
              diagnoses: normalizeDiagnoses(data.diagnoses, 5),
              supportStrategies: normalizeList(data.supportStrategies, 5),
              recommendedNextSteps: normalizeList(data.recommendedNextSteps, 5),
              preferredTeachingModes: normalizeList(data.preferredTeachingModes, 5),
              teachingMethodsUsed: normalizeList(data.teachingMethodsUsed, 5),
              confidenceTrend: normalizeText(data.confidenceTrend),
              learningPace: normalizeText(data.learningPace),
              userTurns: Number(data.userTurns) || 0,
              assistantTurns: Number(data.assistantTurns) || 0
            }
          })
          .filter((report) => report.status !== 'started')
          .sort((first, second) => (second.endedAtMs || second.startedAtMs) - (first.endedAtMs || first.startedAtMs))

        const learnerProfileData = learnerSnapshot.exists()
          ? {
              summary: normalizeText(learnerSnapshot.data()?.lastSessionSummary),
              strengths: normalizeList(learnerSnapshot.data()?.strengths, 5),
              struggles: normalizeList(learnerSnapshot.data()?.struggles, 5),
              misconceptions: normalizeList(learnerSnapshot.data()?.misconceptions, 5),
              diagnoses: normalizeDiagnoses(learnerSnapshot.data()?.diagnoses, 5),
              preferredTeachingModes: normalizeList(learnerSnapshot.data()?.preferredTeachingModes || learnerSnapshot.data()?.preferredModesSeen, 5),
              supportStrategies: normalizeList(learnerSnapshot.data()?.supportStrategies, 5),
              recommendedNextSteps: normalizeList(learnerSnapshot.data()?.recommendedNextSteps, 5),
              topicsSeen: normalizeList(learnerSnapshot.data()?.topicsSeen || learnerSnapshot.data()?.lastTopicsCovered, 8),
              learningPace: normalizeText(learnerSnapshot.data()?.learningPace),
              confidenceTrend: normalizeText(learnerSnapshot.data()?.confidenceTrend),
              currentLessonObjective: normalizeText(learnerSnapshot.data()?.currentLessonObjective),
              lastSessionEndedAt: normalizeTimestamp(learnerSnapshot.data()?.lastSessionEndedAt),
              sessionCount: Number(learnerSnapshot.data()?.sessionCount) || reportList.length
            }
          : null

        const assignmentList = assignmentsSnapshot.docs
          .map((assignmentDoc) => ({ id: assignmentDoc.id, ...assignmentDoc.data() }))
          .filter((assignment) => Array.isArray(assignment.studentIds) && assignment.studentIds.includes(studentId))
          .sort((first, second) => normalizeTimestamp(second.createdAt) - normalizeTimestamp(first.createdAt))

        const assignmentIds = assignmentList.map((assignment) => assignment.id).filter(Boolean)
        const submissionSnapshots = await Promise.all(
          chunkArray(assignmentIds, 10).map((assignmentChunk) => (
            getDocs(query(collection(db, 'assignmentSubmissions'), where('assignmentId', 'in', assignmentChunk)))
          ))
        )

        const studentEmail = primaryMembership.studentEmail || profileData.userEmail || previewStudent.email || ''
        const submissionList = submissionSnapshots
          .flatMap((snapshot) => snapshot.docs.map((submissionDoc) => ({ id: submissionDoc.id, ...submissionDoc.data() })))
          .filter((submission) => {
            const submissionStudentId = submission.studentId || submission.studentID || submission.userId || submission.submittedById
            const submissionEmail = submission.studentEmail || submission.email || submission.userEmail
            return submissionStudentId === studentId || (studentEmail && submissionEmail === studentEmail)
          })

        const assessmentSnapshots = await Promise.all(
          submissionList.map((submission) => getDoc(doc(db, 'assignmentSubmissionAssessments', submission.id)))
        )

        const assessmentMap = assessmentSnapshots.reduce((acc, assessmentSnapshot) => {
          if (!assessmentSnapshot.exists()) return acc
          acc[assessmentSnapshot.id] = { id: assessmentSnapshot.id, ...assessmentSnapshot.data() }
          return acc
        }, {})

        if (!isMounted) return

        setStudent({
          studentId,
          name: primaryMembership.name || profileData.bio || profileData.name || previewStudent.name || 'Student',
          email: studentEmail,
          phone: profileData.phoneNumber || profileData.userPhone || profileData.phone || '',
          course: primaryMembership.course || profileData.course || previewStudent.course || '',
          profilePic: primaryMembership.profilePic || profileData.profilePic || previewStudent.avatar || profilePictureContImage,
          coverPhoto: profileData.coverPhoto || coverPhotoContImage,
          nationality: profileData.nationality || profileData.country || profileData.location || '',
          website: profileData.userWebsite || '',
          github: profileData.userGithub || profileData.github || '',
          about: profileData.aboutMe || profileData.about || profileData.userAbout || '',
          expectation: profileData.whyInterested || profileData.learningGoals || '',
          status: profileData.userStatus || profileData.Status || 'Student',
          cohortDate: primaryMembership.cohortDate || '',
          classNames: classRecords.map((classItem) => classItem.name)
        })
        setStudentClasses(classRecords)
        setFireflyProfile(learnerProfileData)
        setAssignmentLearningProfile(assignmentProfileSnapshot.exists() ? { id: assignmentProfileSnapshot.id, ...assignmentProfileSnapshot.data() } : null)
        setFireflyLearningProfile(fireflyProfileSnapshot.exists() ? { id: fireflyProfileSnapshot.id, ...fireflyProfileSnapshot.data() } : null)
        setFireflyReports(reportList)
        setFireflyReadMap(readsSnapshot.docs.reduce((reads, readDoc) => {
          reads[readDoc.id] = true
          return reads
        }, {}))
        setAssignments(assignmentList.map((assignment) => ({
          ...assignment,
          submissions: submissionList.filter((submission) => submission.assignmentId === assignment.id)
        })))
        setAssignmentAssessments(assessmentMap)
        setSelectedAssignmentReviewId((current) => (
          current && assessmentMap[current]
            ? current
            : Object.values(assessmentMap)
              .filter((item) => normalizeKey(item.status) === 'completed' && item.assessment)
              .sort((first, second) => normalizeTimestamp(second.updatedAt || second.generatedAt || second.createdAt) - normalizeTimestamp(first.updatedAt || first.generatedAt || first.createdAt))[0]?.id || ''
        ))
        setSelectedReportId((current) => current || reportList[0]?.id || '')
      } catch (loadError) {
        console.error('Error loading student profile:', loadError)
        if (!isMounted) return
        setError(loadError.message || 'Could not load this learner profile right now.')
      } finally {
        if (isMounted) setLoading(false)
      }
    })

    return () => {
      isMounted = false
      unsubscribe()
    }
  }, [auth, previewStudent.avatar, previewStudent.course, previewStudent.email, previewStudent.name, refreshKey, studentId])

  const fireflyAnalytics = useMemo(() => {
    const totalSeconds = fireflyReports.reduce((total, report) => total + (report.durationSeconds || 0), 0)
    const totalTopics = Array.from(new Set(fireflyReports.flatMap((report) => report.topicsCovered || []))).length

    return {
      totalSeconds,
      averageDuration: fireflyReports.length ? Math.round(totalSeconds / fireflyReports.length) : 0,
      totalTopics,
      topTopics: getTopCounts(fireflyReports.flatMap((report) => report.topicsCovered || []), 6),
      topTeaching: getTopCounts(fireflyReports.flatMap((report) => report.teachingMethodsUsed || []), 6),
      strengths: Array.from(new Set([
        ...(fireflyProfile?.strengths || []),
        ...fireflyReports.flatMap((report) => report.strengths || [])
      ])).slice(0, 6),
      struggles: Array.from(new Set([
        ...(fireflyProfile?.struggles || []),
        ...fireflyReports.flatMap((report) => report.struggles || [])
      ])).slice(0, 6)
    }
  }, [fireflyProfile, fireflyReports])

  const assignmentProfileView = useMemo(() => {
    if (!assignmentLearningProfile?.profile) return null

    const profile = assignmentLearningProfile.profile || {}
    const analytics = assignmentLearningProfile.analytics || {}
    const profileMeta = assignmentLearningProfile.profileMeta || {}

    return {
      headline: normalizeText(profile.summary?.headline) || 'Assignment learning profile',
      learnerSnapshot: normalizeText(profile.summary?.learnerSnapshot || profile.summary?.momentumNarrative),
      futureOfLearningLens: normalizeText(profile.summary?.futureOfLearningLens),
      momentumNarrative: normalizeText(profile.summary?.momentumNarrative),
      confidence: normalizeText(profile.summary?.confidence) || 'Building signal',
      confidenceReason: normalizeText(profile.summary?.confidenceReason),
      readinessBand: normalizeText(profile.profileSignals?.readinessBand) || 'Building profile',
      evidenceStrength: normalizeText(profileMeta.evidenceStrength) || 'low',
      evidenceSummary: normalizeText(profileMeta.summary),
      sourceCount: Number(profileMeta.sourceCount) || Number(analytics.reviewCount) || 0,
      lastAnalyzedLabel: formatProfileDate(
        assignmentLearningProfile.lastAnalyzedAt
        || assignmentLearningProfile.lastAnalyzedAtMs
        || assignmentLearningProfile.updatedAt
        || assignmentLearningProfile.generatedAt
        || assignmentLearningProfile.updatedAtMs
        || assignmentLearningProfile.generatedAtMs
      ),
      updatedLabel: formatProfileDate(
        assignmentLearningProfile.updatedAt
        || assignmentLearningProfile.generatedAt
        || assignmentLearningProfile.updatedAtMs
        || assignmentLearningProfile.generatedAtMs
      ),
      learningModes: (Array.isArray(profile.profileSignals?.learningModeFit) ? profile.profileSignals.learningModeFit : [])
        .map((item) => ({
          mode: normalizeText(item?.mode),
          fit: normalizeText(item?.fit),
          evidence: normalizeText(item?.evidence),
          score: getProfileFitScore(item?.fit)
        }))
        .filter((item) => item.mode || item.fit || item.evidence),
      rubricAverages: (Array.isArray(analytics.rubricAverages) ? analytics.rubricAverages : []).slice(0, 6),
      averageRubricPercent: Math.round(Number(analytics.averageRubricPercent) || 0),
      changeSinceLastUpdate: {
        summary: normalizeText(profile.changeSinceLastUpdate?.summary),
        newStrengths: normalizeList(profile.changeSinceLastUpdate?.newStrengths, 4),
        newRisks: normalizeList(profile.changeSinceLastUpdate?.newRisks, 4),
        improvedSignals: normalizeList(profile.changeSinceLastUpdate?.improvedSignals, 4),
        declinedSignals: normalizeList(profile.changeSinceLastUpdate?.declinedSignals, 4)
      },
      strongestRecurringDimensions: getProfileBullets(profile.performancePatterns?.strongestRecurringDimensions, 4),
      developingDimensions: getProfileBullets(profile.performancePatterns?.developingDimensions, 4),
      transferPattern: normalizeText(profile.learningIdentity?.transferPattern),
      primaryModes: normalizeList(profile.learningIdentity?.primaryModes, 4),
      strongestHabits: normalizeList(profile.learningIdentity?.strongestHabits, 4),
      frictionPatterns: normalizeList(profile.learningIdentity?.frictionPatterns, 4),
      paceProfile: normalizeText(profile.learningIdentity?.paceProfile),
      confidencePattern: normalizeText(profile.learningIdentity?.confidencePattern),
      consistencyPattern: normalizeText(profile.performancePatterns?.consistencyPattern),
      revisionPattern: normalizeText(profile.performancePatterns?.revisionPattern),
      curriculumReadiness: normalizeText(profile.performancePatterns?.curriculumReadiness),
      interventionPriorities: (Array.isArray(profile.teacherGuidance?.interventionPriorities) ? profile.teacherGuidance.interventionPriorities : []).slice(0, 3),
      nextLessonMoves: normalizeList(profile.teacherGuidance?.nextLessonMoves, 4),
      thisWeekMoves: normalizeList(profile.teacherGuidance?.thisWeekMoves, 4),
      nextAssessmentCycleMoves: normalizeList(profile.teacherGuidance?.nextAssessmentCycleMoves, 4),
      persistence: {
        notUnderstoodYet: normalizeList(profile.diagnosticLens?.persistenceVsUnderstanding?.notUnderstoodYet, 3),
        inconsistentExecution: normalizeList(profile.diagnosticLens?.persistenceVsUnderstanding?.inconsistentExecution, 3),
        rushesOrSkipsChecks: normalizeList(profile.diagnosticLens?.persistenceVsUnderstanding?.rushesOrSkipsChecks, 3),
        interpretation: normalizeText(profile.diagnosticLens?.persistenceVsUnderstanding?.evidenceBasedInterpretation)
      },
      misconceptionRecurrence: getProfileBullets(profile.diagnosticLens?.misconceptionRecurrence, 4),
      actionPlan: {
        keepDoing: normalizeList(profile.studentActionPlan?.keepDoing, 4),
        focusNow: normalizeList(profile.studentActionPlan?.focusNow, 4),
        practiceRoutine: normalizeList(profile.studentActionPlan?.practiceRoutine, 4),
        nextChallenge: normalizeText(profile.studentActionPlan?.nextChallenge)
      },
      fireflyRecommendation: {
        shouldInviteLiveSession: Boolean(profile.fireflyRecommendation?.shouldInviteLiveSession),
        studentSummary: normalizeText(profile.fireflyRecommendation?.studentSummary),
        inviteWithin: normalizeText(profile.fireflyRecommendation?.inviteWithin),
        suggestedSessionLength: normalizeText(profile.fireflyRecommendation?.suggestedSessionLength),
        focusConcepts: getProfileBullets(profile.fireflyRecommendation?.recommendedFocusConcepts, 4)
      },
      studentFacing: {
        encouragement: normalizeText(profile.studentFacing?.encouragement),
        focusNext: normalizeList(profile.studentFacing?.focusNext, 4),
        whatIsGoingWell: normalizeList(profile.studentFacing?.whatIsGoingWell, 4),
        whyThisMatters: normalizeText(profile.studentFacing?.whyThisMatters),
        reflectionPrompt: normalizeText(profile.studentFacing?.reflectionPrompt),
      }
    }
  }, [assignmentLearningProfile])

  const fireflyProfileView = useMemo(() => {
    if (!fireflyLearningProfile?.profile) return null

    const profile = fireflyLearningProfile.profile || {}
    const analytics = fireflyLearningProfile.analytics || {}
    const profileMeta = fireflyLearningProfile.profileMeta || {}

    return {
      headline: normalizeText(profile.summary?.headline) || 'Firefly learning profile',
      learnerSnapshot: normalizeText(profile.summary?.learnerSnapshot || profile.summary?.momentumNarrative),
      futureOfLearningLens: normalizeText(profile.summary?.futureOfLearningLens),
      confidence: normalizeText(profile.summary?.confidence) || 'Building signal',
      confidenceReason: normalizeText(profile.summary?.confidenceReason),
      readinessBand: normalizeText(profile.profileSignals?.readinessBand) || 'Building profile',
      evidenceStrength: normalizeText(profileMeta.evidenceStrength) || 'low',
      evidenceSummary: normalizeText(profileMeta.summary),
      sourceCount: Number(profileMeta.sourceCount) || Number(analytics.sessionCount) || fireflyReports.length,
      lastAnalyzedLabel: formatProfileDate(
        fireflyLearningProfile.lastAnalyzedAt
        || fireflyLearningProfile.lastAnalyzedAtMs
        || fireflyLearningProfile.updatedAt
        || fireflyLearningProfile.generatedAt
        || fireflyLearningProfile.updatedAtMs
        || fireflyLearningProfile.generatedAtMs
      ),
      updatedLabel: formatProfileDate(
        fireflyLearningProfile.updatedAt
        || fireflyLearningProfile.generatedAt
        || fireflyLearningProfile.updatedAtMs
        || fireflyLearningProfile.generatedAtMs
      ),
      learningModes: (Array.isArray(profile.profileSignals?.learningModeFit) ? profile.profileSignals.learningModeFit : [])
        .map((item) => ({
          mode: normalizeText(item?.mode),
          fit: normalizeText(item?.fit),
          evidence: normalizeText(item?.evidence),
          score: getProfileFitScore(item?.fit)
        }))
        .filter((item) => item.mode || item.fit || item.evidence),
      changeSinceLastUpdate: {
        summary: normalizeText(profile.changeSinceLastUpdate?.summary),
        newStrengths: normalizeList(profile.changeSinceLastUpdate?.newStrengths, 4),
        newRisks: normalizeList(profile.changeSinceLastUpdate?.newRisks, 4),
        improvedSignals: normalizeList(profile.changeSinceLastUpdate?.improvedSignals, 4),
        declinedSignals: normalizeList(profile.changeSinceLastUpdate?.declinedSignals, 4)
      },
      sessionIntelligence: {
        preferredTeachingModes: normalizeList(profile.sessionIntelligence?.preferredTeachingModes, 4),
        explanationFormatsThatWork: normalizeList(profile.sessionIntelligence?.explanationFormatsThatWork, 4),
        engagementPattern: normalizeText(profile.sessionIntelligence?.engagementPattern),
        pacePattern: normalizeText(profile.sessionIntelligence?.pacePattern),
        confidencePattern: normalizeText(profile.sessionIntelligence?.confidencePattern)
      },
      conceptMasteryMap: {
        strongestThemes: getProfileBullets(profile.conceptMasteryMap?.strongestThemes, 4),
        fragileThemes: getProfileBullets(profile.conceptMasteryMap?.fragileThemes, 4),
        misconceptionThreads: getProfileBullets(profile.conceptMasteryMap?.misconceptionThreads, 4)
      },
      interventionPriorities: (Array.isArray(profile.teacherGuidance?.interventionPriorities) ? profile.teacherGuidance.interventionPriorities : []).slice(0, 3),
      nextLessonMoves: normalizeList(profile.teacherGuidance?.nextLessonMoves, 4),
      thisWeekMoves: normalizeList(profile.teacherGuidance?.thisWeekMoves, 4),
      nextAssessmentCycleMoves: normalizeList(profile.teacherGuidance?.nextAssessmentCycleMoves, 4),
      tutoringPlaybook: {
        openingMoves: normalizeList(profile.tutoringPlaybook?.openingMoves, 4),
        inSessionMoves: normalizeList(profile.tutoringPlaybook?.inSessionMoves, 4),
        checksForUnderstanding: normalizeList(profile.tutoringPlaybook?.checksForUnderstanding, 4),
        boardAndVisualStrategies: normalizeList(profile.tutoringPlaybook?.boardAndVisualStrategies, 4),
      },
      misconceptionRecurrence: getProfileBullets(profile.diagnosticLens?.misconceptionRecurrence, 4),
      fireflyRecommendation: {
        shouldInviteLiveSession: Boolean(profile.fireflyRecommendation?.shouldInviteLiveSession),
        urgency: normalizeText(profile.fireflyRecommendation?.urgency) || 'low',
        studentSummary: normalizeText(profile.fireflyRecommendation?.studentSummary),
        outreachReason: normalizeText(profile.fireflyRecommendation?.outreachReason || profile.fireflyRecommendation?.teacherReason),
        focusConcepts: getProfileBullets(profile.fireflyRecommendation?.recommendedFocusConcepts, 4),
        sessionGoals: normalizeList(profile.fireflyRecommendation?.sessionGoals, 4),
        inviteWithin: normalizeText(profile.fireflyRecommendation?.inviteWithin)
      },
      studentFacing: {
        encouragement: normalizeText(profile.studentFacing?.encouragement),
        focusNext: normalizeList(profile.studentFacing?.focusNext, 4),
        whatIsGoingWell: normalizeList(profile.studentFacing?.whatIsGoingWell, 4),
        whyThisMatters: normalizeText(profile.studentFacing?.whyThisMatters),
        reflectionPrompt: normalizeText(profile.studentFacing?.reflectionPrompt),
      }
    }
  }, [fireflyLearningProfile, fireflyReports.length])

  const unreadFireflyCount = useMemo(() => (
    fireflyReports.filter((report) => !fireflyReadMap[buildFireflyReadId(instructorId, studentId, report.id)]).length
  ), [fireflyReadMap, fireflyReports, instructorId, studentId])

  const assignmentReviewReports = useMemo(() => (
    assignments.flatMap((assignment) => (
      assignment.submissions
        .map((submission) => ({
          assignment,
          submission,
          assessment: assignmentAssessments[submission.id] || null
        }))
        .filter((item) => normalizeKey(item.assessment?.status) === 'completed' && item.assessment?.assessment)
    )).sort((first, second) => (
      normalizeTimestamp(second.submission.updatedAt || second.submission.createdAt || second.submission.submittedAt)
      - normalizeTimestamp(first.submission.updatedAt || first.submission.createdAt || first.submission.submittedAt)
    ))
  ), [assignmentAssessments, assignments])

  const selectedAssignmentReview = useMemo(() => (
    assignmentReviewReports.find((item) => item.submission.id === selectedAssignmentReviewId) || null
  ), [assignmentReviewReports, selectedAssignmentReviewId])

  const selectedAssignmentAssessment = selectedAssignmentReview?.assessment?.assessment || null

  const selectedAssignmentRubricChart = useMemo(() => (
    getAssessmentChartItems(selectedAssignmentAssessment?.rubric)
  ), [selectedAssignmentAssessment])

  const selectedReport = useMemo(() => (
    fireflyReports.find((report) => report.id === selectedReportId) || null
  ), [fireflyReports, selectedReportId])

  const assignmentStats = useMemo(() => ({
    total: assignments.length,
    submitted: assignments.filter((assignment) => assignment.submissions.length > 0).length,
    pending: assignments.filter((assignment) => assignment.submissions.length === 0).length
  }), [assignments])

  const fireflyDiagnosisMap = useMemo(() => {
    if (fireflyProfile?.diagnoses?.length) return fireflyProfile.diagnoses

    return fireflyReports
      .flatMap((report) => report.diagnoses || [])
      .filter((item, index, list) => {
        const key = `${item.topic}-${item.subtopic}-${item.misconception}`
        return list.findIndex((candidate) => `${candidate.topic}-${candidate.subtopic}-${candidate.misconception}` === key) === index
      })
      .slice(0, 6)
  }, [fireflyProfile, fireflyReports])

  useEffect(() => {
    if (!instructorId || !studentId || !selectedReport?.id) return

    const readId = buildFireflyReadId(instructorId, studentId, selectedReport.id)
    if (fireflyReadMap[readId]) return

    setDoc(doc(db, 'fireflyReportReads', readId), {
      instructorId,
      studentId,
      reportId: selectedReport.id,
      readAt: serverTimestamp()
    }, { merge: true }).then(() => {
      setFireflyReadMap((current) => ({ ...current, [readId]: true }))
    }).catch((markError) => {
      console.error('Error marking Firefly report as read:', markError)
    })
  }, [fireflyReadMap, instructorId, selectedReport, studentId])

  if (loading) {
    return (
      <div className='studentProfilePage'>
        <div className='studentProfileState'>
          <div className='spinner-border text-dark' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
          <span>Loading learner profile...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className='studentProfilePage'>
        <div className='studentProfileState'>
          <h4>Learner profile unavailable</h4>
          <p>{error}</p>
          <button type='button' className='studentProfileBackButton' onClick={() => navigate(-1)}>
            Back
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className='studentProfilePage'>
      <section className='studentProfileShell'>
        <button type='button' className='studentProfileBackButton studentProfileBackButtonTop' onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to search
        </button>

        <div className='studentProfileToolbar'>
          <button type='button' className='studentProfileRefreshButton' onClick={handleRefreshLearnerProfile} disabled={loading || refreshingProfile}>
            {refreshingProfile ? 'Rebuilding...' : loading ? 'Refreshing...' : 'Refresh and rebuild'}
          </button>
        </div>

        {refreshMessage && (
          <div className={`studentProfileBanner ${refreshMessage.toLowerCase().includes('could not') || refreshMessage.toLowerCase().includes('sign in') ? 'studentProfileBannerError' : ''}`}>
            {refreshMessage}
          </div>
        )}

        <div className='studentProfileHero'>
          <div className='studentProfileCover'>
            <img src={student.coverPhoto || coverPhotoContImage} alt={student.name} />
            <div className='studentProfileCoverShade' />
          </div>

          <div className='studentProfileHeroBody'>
            <div className='studentProfileHeroIdentity'>
              <span className='studentProfileAvatar'>
                <img src={student.profilePic || profilePictureContImage} alt={student.name} />
              </span>

              <div className='studentProfileHeroCopy'>
                <span className='studentProfileEyebrow'>Student profile</span>
                <h2>{student.name}</h2>
                <p>{student.email || 'Email not added'}</p>
                <div className='studentProfileHeroTags'>
                  <span>{student.course || 'Course not assigned'}</span>
                  <span>{student.status || 'Student'}</span>
                  <span>{student.cohortDate || 'Cohort not set'}</span>
                </div>
              </div>
            </div>

            <div className='studentProfileHeroActions'>
              <button type='button' className='studentProfileMessageButton' onClick={openWorkspaceMessage}>
                <FontAwesomeIcon icon={faMessage} />
                Message student
              </button>
            </div>
          </div>
        </div>

        <div className='studentProfileTabs'>
          {[
            ['overview', 'Overview'],
            ['classes', 'Classes'],
            ['assignments', 'Assignments'],
            ['firefly', 'Firefly']
          ].map(([tabId, label]) => (
            <button
              key={tabId}
              type='button'
              className={activeTab === tabId ? 'active' : ''}
              onClick={() => setActiveTab(tabId)}
            >
              {label}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <section className='studentProfileGrid'>
            <article className='studentProfileCard studentProfileCardWide'>
              <div className='studentProfileCardHead'>
                <div>
                  <span className='studentProfileEyebrow'>Snapshot</span>
                  <h4>What the instructor should know first</h4>
                </div>
              </div>
              <p className='studentProfileLead'>
                {student.about || fireflyProfile?.summary || `${student.name} is currently studying ${student.course || 'their assigned pathway'} with ${studentClasses.length} connected class${studentClasses.length === 1 ? '' : 'es'}.`}
              </p>
              <div className='studentProfileQuickGrid'>
                <div>
                  <span>Current course</span>
                  <strong>{student.course || 'Not assigned'}</strong>
                </div>
                <div>
                  <span>Primary cohort</span>
                  <strong>{student.cohortDate || 'Not set'}</strong>
                </div>
                <div>
                  <span>Assignment review signal</span>
                  <strong>{assignmentProfileView?.readinessBand || assignmentProfileView?.headline || 'No reviewed work yet'}</strong>
                </div>
                <div>
                  <span>Learning pace</span>
                  <strong>{fireflyProfile?.learningPace || 'Still being learned'}</strong>
                </div>
              </div>
            </article>

            <article className='studentProfileCard'>
              <div className='studentProfileCardHead'>
                <div>
                  <span className='studentProfileEyebrow'>Contact</span>
                  <h4>Reach this student</h4>
                </div>
              </div>
              <div className='studentProfileContactList'>
                <div>
                  <FontAwesomeIcon icon={faEnvelope} />
                  <div>
                    <span>Email</span>
                    <p>{student.email || 'Not provided'}</p>
                  </div>
                </div>
                <div>
                  <FontAwesomeIcon icon={faPhone} />
                  <div>
                    <span>Phone</span>
                    <p>{student.phone || 'Not provided'}</p>
                  </div>
                </div>
                <div>
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                  <div>
                    <span>Location</span>
                    <p>{student.nationality || 'Not provided'}</p>
                  </div>
                </div>
                <div>
                  <FontAwesomeIcon icon={faGlobe} />
                  <div>
                    <span>Website</span>
                    {student.website ? (
                      <a href={student.website} target='_blank' rel='noreferrer'>{student.website}</a>
                    ) : (
                      <p>Not provided</p>
                    )}
                  </div>
                </div>
              </div>
            </article>

            <article className='studentProfileCard'>
              <div className='studentProfileCardHead'>
                <div>
                  <span className='studentProfileEyebrow'>Class load</span>
                  <h4>Connected classes</h4>
                </div>
              </div>
              <div className='studentProfileStack'>
                {studentClasses.map((classItem) => (
                  <div key={classItem.id} className='studentProfileMiniRow'>
                    <div>
                      <strong>{classItem.name}</strong>
                      <p>{classItem.course || 'Course not set'}</p>
                    </div>
                    <span>{classItem.startTime ? `${classItem.startTime}${classItem.endTime ? ` - ${classItem.endTime}` : ''}` : 'Schedule pending'}</span>
                  </div>
                ))}
              </div>
            </article>

            <article className='studentProfileCard'>
              <div className='studentProfileCardHead'>
                <div>
                  <span className='studentProfileEyebrow'>Learning signals</span>
                  <h4>Firefly summary</h4>
                </div>
              </div>
              <div className='studentProfileSignalList'>
                <div>
                  <span>Strengths</span>
                  <p>{fireflyAnalytics.strengths.slice(0, 3).join(', ') || 'Firefly is still building this signal.'}</p>
                </div>
                <div>
                  <span>Gaps</span>
                  <p>{fireflyAnalytics.struggles.slice(0, 3).join(', ') || 'No clear gaps captured yet.'}</p>
                </div>
                <div>
                  <span>Next teaching move</span>
                  <p>{fireflyProfile?.recommendedNextSteps?.[0] || 'Open Firefly reports to plan the next support move.'}</p>
                </div>
              </div>
            </article>

            {(assignmentProfileView || fireflyProfileView) && (
              <article className='studentProfileCard studentProfileCardWide studentProfileIntelligenceBoard'>
                <div className='studentProfileCardHead'>
                  <div>
                    <span className='studentProfileEyebrow'>Intelligence board</span>
                    <h4>Daily learner profile snapshots</h4>
                  </div>
                </div>

                <div className='studentProfileInsightDeck'>
                  {assignmentProfileView && (
                    <section className='studentProfileInsightCard studentProfileInsightCardAssignment'>
                      <div className='studentProfileInsightTop'>
                        <div>
                          <span className='studentProfileEyebrow'>Assignments</span>
                          <h5>{assignmentProfileView.headline}</h5>
                        </div>
                        <strong>{assignmentProfileView.readinessBand}</strong>
                      </div>
                      <p>{assignmentProfileView.learnerSnapshot || 'Assignment reviews are starting to reveal how this learner performs independently.'}</p>
                      <div className='studentProfileInsightMeta'>
                        <span>{assignmentProfileView.sourceCount} reviews</span>
                        <span>{assignmentProfileView.evidenceStrength} evidence</span>
                        <span>{assignmentProfileView.lastAnalyzedLabel || assignmentProfileView.updatedLabel}</span>
                      </div>
                      <div className='studentProfileInsightFooter'>
                        <small>{assignmentProfileView.interventionPriorities?.[0]?.firstMove || assignmentProfileView.nextLessonMoves?.[0] || 'Open the assignments tab to see the ranked intervention moves.'}</small>
                      </div>
                    </section>
                  )}

                  {fireflyProfileView && (
                    <section className='studentProfileInsightCard studentProfileInsightCardFirefly'>
                      <div className='studentProfileInsightTop'>
                        <div>
                          <span className='studentProfileEyebrow'>Firefly</span>
                          <h5>{fireflyProfileView.headline}</h5>
                        </div>
                        <strong>{fireflyProfileView.readinessBand}</strong>
                      </div>
                      <p>{fireflyProfileView.learnerSnapshot || 'Firefly sessions are building a live tutoring profile for this learner.'}</p>
                      <div className='studentProfileInsightMeta'>
                        <span>{fireflyProfileView.sourceCount} sessions</span>
                        <span>{fireflyProfileView.evidenceStrength} evidence</span>
                        <span>{fireflyProfileView.lastAnalyzedLabel || fireflyProfileView.updatedLabel}</span>
                      </div>
                      <div className='studentProfileInsightFooter'>
                        <small>{fireflyProfileView.interventionPriorities?.[0]?.firstMove || fireflyProfileView.nextLessonMoves?.[0] || 'Open the Firefly tab to see the tutoring playbook and misconception threads.'}</small>
                      </div>
                    </section>
                  )}
                </div>
              </article>
            )}
          </section>
        )}

        {activeTab === 'classes' && (
          <section className='studentProfileClassGrid'>
            {studentClasses.map((classItem) => (
              <article key={classItem.id} className='studentProfileClassCard'>
                <div className='studentProfileClassTop'>
                  <div>
                    <span className='studentProfileEyebrow'>Class</span>
                    <h4>{classItem.name}</h4>
                    <p>{classItem.course || 'Course not set'}</p>
                  </div>
                  <span className='studentProfileClassBadge'>{classItem.cohortDate || 'Cohort pending'}</span>
                </div>

                <div className='studentProfileClassMeta'>
                  <div>
                    <span>Start date</span>
                    <strong>{formatCalendarDate(classItem.startDate)}</strong>
                  </div>
                  <div>
                    <span>Session window</span>
                    <strong>{classItem.startTime ? `${classItem.startTime}${classItem.endTime ? ` - ${classItem.endTime}` : ''}` : 'Not scheduled'}</strong>
                  </div>
                  <div>
                    <span>Room</span>
                    <strong>{classItem.roomID || 'Not set'}</strong>
                  </div>
                </div>
              </article>
            ))}
          </section>
        )}

        {activeTab === 'assignments' && (
          <section className='studentProfileAssignments'>
            <div className='studentProfileFireflyTop'>
              <div>
                <span className='studentProfileEyebrow'>Learning profile</span>
                <h3>Assignment intelligence for {student.name}</h3>
                <p>Use the general report to understand the learner pattern, then open individual reviewed submissions for the detailed report.</p>
              </div>

              <div className='studentProfileFireflyTabs'>
                <button
                  type='button'
                  className={assignmentView === 'general' ? 'active' : ''}
                  onClick={() => setAssignmentView('general')}
                >
                  General report
                </button>
                <button
                  type='button'
                  className={assignmentView === 'individual' ? 'active' : ''}
                  onClick={() => setAssignmentView('individual')}
                >
                  Individual reviews
                  {assignmentReviewReports.length > 0 && <span>{assignmentReviewReports.length}</span>}
                </button>
              </div>
            </div>

            {assignmentView === 'general' && (
              <>
                {assignmentProfileView && (
                  <article className='studentProfilePortalCard studentProfileAssignmentProfileCard'>
                    <div className='studentProfilePortalHead'>
                      <div>
                        <span className='studentProfileEyebrow'>Generated learning profile</span>
                        <h4>{assignmentProfileView.headline}</h4>
                        <p>{assignmentProfileView.learnerSnapshot || 'A daily learner profile built from all reviewed assignments.'}</p>
                      </div>
                    </div>

                    <div className='studentProfilePortalMetrics'>
                      <div>
                        <span>Readiness band</span>
                        <strong>{assignmentProfileView.readinessBand}</strong>
                      </div>
                      <div>
                        <span>Evidence strength</span>
                        <strong>{assignmentProfileView.evidenceStrength}</strong>
                      </div>
                      <div>
                        <span>Reviewed assignments</span>
                        <strong>{assignmentProfileView.sourceCount}</strong>
                      </div>
                      <div>
                        <span>Last analysis</span>
                        <strong>{assignmentProfileView.lastAnalyzedLabel}</strong>
                      </div>
                    </div>

                    {assignmentProfileView.evidenceSummary && (
                      <div className='studentProfilePortalSummary'>
                        <h5>Profile freshness</h5>
                        <p>{assignmentProfileView.evidenceSummary}</p>
                      </div>
                    )}

                    {assignmentProfileView.fireflyRecommendation.shouldInviteLiveSession && (
                      <div className='studentProfilePortalSummary'>
                        <h5>Firefly support recommendation</h5>
                        <p>{assignmentProfileView.fireflyRecommendation.studentSummary || 'A live Firefly session is recommended so the learner can get guided support on the next concept lift.'}</p>
                        <div className='studentProfileChipCloud'>
                          {assignmentProfileView.fireflyRecommendation.focusConcepts.map((item) => (
                            <span key={`assignment-firefly-${item.label}`}>{item.label}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className='studentProfilePortalTwoCol'>
                      <div>
                        <h5>What changed since last update</h5>
                        <p>{assignmentProfileView.changeSinceLastUpdate.summary || 'The next rebuild will explain what improved, what declined, and what appeared as a new signal.'}</p>
                        <div className='studentProfileChipCloud'>
                          {assignmentProfileView.changeSinceLastUpdate.newStrengths.map((item) => <span key={`assignment-strength-${item}`}>{item}</span>)}
                          {assignmentProfileView.changeSinceLastUpdate.improvedSignals.map((item) => <span key={`assignment-improved-${item}`}>{item}</span>)}
                          {assignmentProfileView.changeSinceLastUpdate.newRisks.map((item) => <span key={`assignment-risk-${item}`}>{item}</span>)}
                          {assignmentProfileView.changeSinceLastUpdate.declinedSignals.map((item) => <span key={`assignment-declined-${item}`}>{item}</span>)}
                        </div>
                      </div>

                      <div>
                        <h5>Intervention priority</h5>
                        <div className='studentProfilePriorityStack'>
                          {(assignmentProfileView.interventionPriorities.length
                            ? assignmentProfileView.interventionPriorities
                            : [{ rank: 1, label: 'No priority locked in yet', whyItMatters: 'The profile needs more repeated evidence before ranking intervention priorities.', firstMove: '' }]).map((item) => (
                            <div key={`assignment-priority-${item.rank}-${item.label}`} className='studentProfilePriorityCard'>
                              <strong>{item.rank}. {item.label}</strong>
                              {item.whyItMatters ? <p>{item.whyItMatters}</p> : null}
                              {item.firstMove ? <small>{item.firstMove}</small> : null}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className='studentProfilePortalTwoCol'>
                      <div>
                        <h5>Rubric score profile</h5>
                        <div className='studentProfileFitBars'>
                          {(assignmentProfileView.rubricAverages.length
                            ? assignmentProfileView.rubricAverages
                            : [{ label: 'No rubric data yet', averagePercent: 0, count: 0 }]).map((item) => (
                            <div key={`rubric-bar-${item.label}`} className='studentProfileFitRow'>
                              <div className='studentProfileFitTop'>
                                <span>{item.label}</span>
                                <small>{item.averagePercent}%</small>
                              </div>
                              <i style={{ width: `${Math.max(8, item.averagePercent)}%` }} />
                              <p>{item.count ? `${item.count} reviewed assignment${item.count === 1 ? '' : 's'} contributed to this signal.` : 'Waiting for scored rubric evidence.'}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h5>Learning mode fit</h5>
                        <div className='studentProfileFitBars'>
                          {(assignmentProfileView.learningModes.length
                            ? assignmentProfileView.learningModes
                            : [{ mode: 'Still building signal', fit: 'low', score: 12, evidence: 'The profile needs more reviewed assignments before it can rank the strongest delivery mode.' }]).map((item) => (
                            <div key={`assignment-fit-${item.mode}`} className='studentProfileFitRow'>
                              <div className='studentProfileFitTop'>
                                <span>{item.mode}</span>
                                <small>{item.fit}</small>
                              </div>
                              <i style={{ width: `${Math.max(8, item.score)}%` }} />
                              {item.evidence ? <p>{item.evidence}</p> : null}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className='studentProfilePortalTwoCol'>
                      <div>
                        <h5>Performance pattern map</h5>
                        <div className='studentProfileSignalDetail'>
                          <strong>Strongest recurring dimensions</strong>
                          <p>{assignmentProfileView.strongestRecurringDimensions.map((item) => item.label).join(', ') || 'No strongest recurring dimension has been locked in yet.'}</p>
                        </div>
                        <div className='studentProfileSignalDetail'>
                          <strong>Developing dimensions</strong>
                          <p>{assignmentProfileView.developingDimensions.map((item) => item.label).join(', ') || 'No developing dimension has been locked in yet.'}</p>
                        </div>
                        <div className='studentProfileSignalDetail'>
                          <strong>Transfer pattern</strong>
                          <p>{assignmentProfileView.transferPattern || 'Transfer across prompts and assignments is still being mapped.'}</p>
                        </div>
                      </div>

                      <div>
                        <h5>Persistence vs understanding</h5>
                        <div className='studentProfileSignalDetail'>
                          <strong>Does not understand yet</strong>
                          <p>{assignmentProfileView.persistence.notUnderstoodYet.join(', ') || 'No persistent pattern locked in yet.'}</p>
                        </div>
                        <div className='studentProfileSignalDetail'>
                          <strong>Understands but lacks consistency</strong>
                          <p>{assignmentProfileView.persistence.inconsistentExecution.join(', ') || 'No recurring inconsistency captured yet.'}</p>
                        </div>
                        <div className='studentProfileSignalDetail'>
                          <strong>Knows it but rushes</strong>
                          <p>{assignmentProfileView.persistence.rushesOrSkipsChecks.join(', ') || 'No repeated rushing pattern captured yet.'}</p>
                        </div>
                        {assignmentProfileView.persistence.interpretation ? (
                          <p className='studentProfileNarrative'>{assignmentProfileView.persistence.interpretation}</p>
                        ) : null}
                      </div>
                    </div>

                    <div className='studentProfilePortalTwoCol'>
                      <div>
                        <h5>Teacher moves by timeframe</h5>
                        <div className='studentProfileMoveTimeline'>
                          <div>
                            <span>Next lesson</span>
                            <p>{assignmentProfileView.nextLessonMoves.join(', ') || 'No next-lesson move locked in yet.'}</p>
                          </div>
                          <div>
                            <span>This week</span>
                            <p>{assignmentProfileView.thisWeekMoves.join(', ') || 'No weekly move surfaced yet.'}</p>
                          </div>
                          <div>
                            <span>Next assessment cycle</span>
                            <p>{assignmentProfileView.nextAssessmentCycleMoves.join(', ') || 'No assessment-cycle move surfaced yet.'}</p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h5>Student-facing signal</h5>
                        <p>{assignmentProfileView.studentFacing.encouragement || 'A clearer student-facing message will appear here as soon as the learning profile becomes more stable.'}</p>
                        <div className='studentProfileSignalDetail'>
                          <strong>What is going well</strong>
                          <p>{assignmentProfileView.studentFacing.whatIsGoingWell.join(', ') || 'No recurring win has been surfaced yet.'}</p>
                        </div>
                        <div className='studentProfileSignalDetail'>
                          <strong>Focus next</strong>
                          <p>{assignmentProfileView.studentFacing.focusNext.join(', ') || 'No student-facing focus has been surfaced yet.'}</p>
                        </div>
                        {(assignmentProfileView.studentFacing.whyThisMatters || assignmentProfileView.studentFacing.reflectionPrompt) && (
                          <div className='studentProfileSignalDetail'>
                            {assignmentProfileView.studentFacing.whyThisMatters && <p><strong>Why this matters:</strong> {assignmentProfileView.studentFacing.whyThisMatters}</p>}
                            {assignmentProfileView.studentFacing.reflectionPrompt && <p><strong>Reflection prompt:</strong> {assignmentProfileView.studentFacing.reflectionPrompt}</p>}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className='studentProfilePortalTwoCol'>
                      <div>
                        <h5>Misconceptions to watch</h5>
                        <div className='studentProfileDiagnosisList'>
                          {(assignmentProfileView.misconceptionRecurrence.length
                            ? assignmentProfileView.misconceptionRecurrence
                            : [{ label: 'No recurring misconception yet', evidence: 'Repeated misconception patterns will be listed here once they appear across more than one reviewed assignment.' }]).map((item, index) => (
                            <article key={`assignment-misconception-${index}`} className='studentProfileDiagnosisCard'>
                              <div className='studentProfileDiagnosisBlock'>
                                <span>Pattern</span>
                                <p>{item.label}</p>
                              </div>
                              {item.evidence ? (
                                <div className='studentProfileDiagnosisBlock'>
                                  <span>Evidence</span>
                                  <p>{item.evidence}</p>
                                </div>
                              ) : null}
                              {item.action ? (
                                <div className='studentProfileDiagnosisBlock'>
                                  <span>Recommended move</span>
                                  <p>{item.action}</p>
                                </div>
                              ) : null}
                            </article>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h5>Learning momentum</h5>
                        <div className='studentProfileMoveTimeline'>
                          <div>
                            <span>Pace profile</span>
                            <p>{assignmentProfileView.paceProfile || 'Pace is still being mapped from repeated reviewed work.'}</p>
                          </div>
                          <div>
                            <span>Consistency pattern</span>
                            <p>{assignmentProfileView.consistencyPattern || 'Consistency across reviewed assignments is still being mapped.'}</p>
                          </div>
                          <div>
                            <span>Future of learning lens</span>
                            <p>{assignmentProfileView.futureOfLearningLens || assignmentProfileView.momentumNarrative || 'This section will connect the learner’s current patterns to future independent learning readiness.'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </article>
                )}

                <div className='studentProfileAssignmentStats'>
                  <div>
                    <span>Assigned</span>
                    <strong>{assignmentStats.total}</strong>
                  </div>
                  <div>
                    <span>Submitted</span>
                    <strong>{assignmentStats.submitted}</strong>
                  </div>
                  <div>
                    <span>Pending</span>
                    <strong>{assignmentStats.pending}</strong>
                  </div>
                  <div>
                    <span>Reviewed</span>
                    <strong>{assignmentReviewReports.length}</strong>
                  </div>
                </div>

                {assignments.length === 0 ? (
                  <div className='studentProfileEmpty'>
                    <h4>No assignments yet</h4>
                    <p>This learner has not been targeted by any instructor assignment yet.</p>
                  </div>
                ) : (
                  <div className='studentProfileAssignmentGrid'>
                    {assignments.map((assignment) => {
                      const latestSubmission = [...assignment.submissions]
                        .sort((first, second) => normalizeTimestamp(second.updatedAt || second.createdAt || second.submittedAt) - normalizeTimestamp(first.updatedAt || first.createdAt || first.submittedAt))[0]
                      const submissionStatus = latestSubmission ? getSubmissionStatus(latestSubmission) : 'pending'
                      const submissionLinks = getSubmissionLinks(latestSubmission)

                      return (
                        <article key={assignment.id} className='studentProfileAssignmentCard'>
                          <div className='studentProfileAssignmentTop'>
                            <div>
                              <span className='studentProfileEyebrow'>Assignment</span>
                              <h4>{assignment.title || 'Untitled assignment'}</h4>
                              <p>{assignment.courseName || student.course || 'Course not set'}</p>
                            </div>
                            <span className={`studentProfileStatus studentProfileStatus-${submissionStatus.includes('review') ? 'reviewed' : submissionStatus.includes('revision') ? 'revision' : latestSubmission ? 'submitted' : 'pending'}`}>
                              {latestSubmission ? (latestSubmission.status || latestSubmission.reviewStatus || 'Submitted') : 'Pending'}
                            </span>
                          </div>

                          <div className='studentProfileAssignmentMeta'>
                            <div>
                              <span>Due</span>
                              <strong>{assignment.dueDate ? formatCalendarDate(assignment.dueDate) : 'No due date'}</strong>
                            </div>
                            <div>
                              <span>Week</span>
                              <strong>{getAssignmentWeekLabel(assignment) || 'Not linked'}</strong>
                            </div>
                            <div>
                              <span>Type</span>
                              <strong>{getAssignmentTypeLabel(assignment)}</strong>
                            </div>
                            <div>
                              <span>Submission</span>
                              <strong>{latestSubmission ? formatCalendarDate(latestSubmission.updatedAt || latestSubmission.createdAt || latestSubmission.submittedAt) : 'Awaiting work'}</strong>
                            </div>
                          </div>

                          <div
                            className='studentProfileAssignmentInstructions'
                            dangerouslySetInnerHTML={{
                              __html: sanitizeRichTextHtml(
                                assignment.instructionsHtml
                                || assignment.instructions
                                || (assignment.promptItems?.length
                                  ? assignment.promptItems.map((prompt, index) => `<p><strong>Q${index + 1}.</strong> ${typeof prompt === 'string' ? prompt : (prompt?.html || prompt?.text || '')}</p>`).join('')
                                  : '<p>No assignment instructions were added.</p>')
                              )
                            }}
                          />

                          {assignment.resourceLink && (
                            <a href={assignment.resourceLink} target='_blank' rel='noreferrer' className='studentProfileInlineLink'>
                              Open assignment resource
                            </a>
                          )}

                          {submissionLinks.length > 0 && (
                            <div className='studentProfileLinkRow'>
                              {submissionLinks.map(([label, value]) => (
                                <a key={`${assignment.id}-${label}`} href={value} target='_blank' rel='noreferrer'>
                                  {label}
                                </a>
                              ))}
                            </div>
                          )}
                        </article>
                      )
                    })}
                  </div>
                )}
              </>
            )}

            {assignmentView === 'individual' && (
              assignmentReviewReports.length === 0 ? (
                <div className='studentProfileEmpty'>
                  <h4>No reviewed assignment reports yet</h4>
                  <p>Once reviewed submissions are available for this learner, the detailed individual reports will appear here.</p>
                </div>
              ) : selectedAssignmentReview && selectedAssignmentAssessment ? (
                <div className='studentProfileFireflyReader'>
                  <button type='button' className='studentProfileBackButton' onClick={() => setSelectedAssignmentReviewId('')}>
                    <FontAwesomeIcon icon={faArrowLeft} />
                    Back to review list
                  </button>

                  <article className='studentProfilePortalCard'>
                    <div className='studentProfilePortalHead'>
                      <div>
                        <span className='studentProfileEyebrow'>Individual learner review</span>
                        <h4>{selectedAssignmentReview.assignment.title || 'Assignment review'}</h4>
                        <p>{selectedAssignmentReview.submission.studentName || selectedAssignmentReview.submission.name || student.name} submitted on {formatProfileDate(selectedAssignmentReview.submission.updatedAt || selectedAssignmentReview.submission.createdAt || selectedAssignmentReview.submission.submittedAt)}</p>
                      </div>
                    </div>

                    <div className='assignmentsAssessmentPanel'>
                      <div className='assignmentsAssessmentGrid'>
                        <article className='assignmentsAssessmentCard assignmentsAssessmentCardWide'>
                          <span>Instructor summary</span>
                          <h5>{selectedAssignmentAssessment.summary?.headline || 'Detailed learner assessment'}</h5>
                          <p>{selectedAssignmentAssessment.summary?.learnerSnapshot || selectedAssignmentAssessment.summary?.futureOfLearningLens || 'No summary was generated yet.'}</p>
                          <div className='assignmentsAssessmentMeta'>
                            <strong>{selectedAssignmentAssessment.summary?.confidence || 'Confidence pending'}</strong>
                            <span>{selectedAssignmentAssessment.summary?.confidenceReason || selectedAssignmentAssessment.summary?.evidenceCoverage || 'Evidence coverage will appear here.'}</span>
                          </div>
                        </article>

                        <article className='assignmentsAssessmentCard'>
                          <span>Future readiness</span>
                          <h5>{selectedAssignmentAssessment.summary?.futureReadiness || 'Signal pending'}</h5>
                          <p>{selectedAssignmentAssessment.summary?.overallAssessment || 'The system will explain the learner trajectory here.'}</p>
                        </article>

                        {normalizeText(selectedAssignmentAssessment.summary?.curriculumAlignment) && (
                          <article className='assignmentsAssessmentCard'>
                            <span>Curriculum alignment</span>
                            <h5>{getAssignmentWeekLabel(selectedAssignmentReview.assignment) || 'Selected learning week'}</h5>
                            <p>{selectedAssignmentAssessment.summary.curriculumAlignment}</p>
                          </article>
                        )}

                        {selectedAssignmentRubricChart.length > 0 && (
                          <article className='assignmentsAssessmentCard assignmentsAssessmentCardWide'>
                            <span>Performance snapshot</span>
                            <h5>Rubric score profile</h5>
                            <div className='assignmentsAssessmentChart' aria-label='Rubric performance chart'>
                              {selectedAssignmentRubricChart.map((item) => (
                                <div key={item.id} className='assignmentsAssessmentChartColumn'>
                                  <strong>{renderAssessmentScore(item.score, item.outOf)}</strong>
                                  <div className='assignmentsAssessmentChartBar'>
                                    <div style={{ height: `${Math.max(item.percent, 10)}%` }} />
                                  </div>
                                  <span title={item.label}>{item.label}</span>
                                </div>
                              ))}
                            </div>
                          </article>
                        )}

                        {normalizeAssessmentList(selectedAssignmentAssessment.rubric).length > 0 && (
                          <article className='assignmentsAssessmentCard assignmentsAssessmentCardWide'>
                            <span>Rubric dimensions</span>
                            <div className='assignmentsAssessmentList'>
                              {selectedAssignmentAssessment.rubric.map((item, index) => (
                                <div key={`${item.id || item.label}-${index}`}>
                                  <strong>{item.label} • {renderAssessmentScore(item.score, item.outOf || 5)}</strong>
                                  <div className='assignmentsAssessmentMeter' aria-hidden='true'>
                                    <div style={{ width: `${getAssessmentScorePercent(item.score, item.outOf || 5)}%` }} />
                                  </div>
                                  <p>{item.rationale}</p>
                                  {item.nextMove && <small>Next move: {item.nextMove}</small>}
                                </div>
                              ))}
                            </div>
                          </article>
                        )}

                        {normalizeAssessmentList(selectedAssignmentAssessment.strengths).length > 0 && (
                          <article className='assignmentsAssessmentCard'>
                            <span>Strengths</span>
                            <div className='assignmentsAssessmentList'>
                              {selectedAssignmentAssessment.strengths.map((item, index) => (
                                <div key={`${item.title}-${index}`}>
                                  <strong>{item.title}</strong>
                                  <p>{item.whyItMatters}</p>
                                  {item.evidence && <small>Evidence: {item.evidence}</small>}
                                </div>
                              ))}
                            </div>
                          </article>
                        )}

                        {normalizeAssessmentList(selectedAssignmentAssessment.growthAreas).length > 0 && (
                          <article className='assignmentsAssessmentCard'>
                            <span>Growth areas</span>
                            <div className='assignmentsAssessmentList'>
                              {selectedAssignmentAssessment.growthAreas.map((item, index) => (
                                <div key={`${item.title}-${index}`}>
                                  <strong>{item.title}</strong>
                                  <p>{item.whyItMatters}</p>
                                  {item.intervention && <small>Intervention: {item.intervention}</small>}
                                </div>
                              ))}
                            </div>
                          </article>
                        )}

                        {normalizeAssessmentList(selectedAssignmentAssessment.misconceptions).length > 0 && (
                          <article className='assignmentsAssessmentCard assignmentsAssessmentCardWide'>
                            <span>Misconceptions and teaching moves</span>
                            <div className='assignmentsAssessmentList'>
                              {selectedAssignmentAssessment.misconceptions.map((item, index) => (
                                <div key={`${item.concept}-${index}`}>
                                  <strong>{item.concept}</strong>
                                  <p>{item.correction}</p>
                                  {item.teachingMove && <small>Teaching move: {item.teachingMove}</small>}
                                </div>
                              ))}
                            </div>
                          </article>
                        )}

                        {normalizeAssessmentList(selectedAssignmentAssessment.promptFindings).length > 0 && (
                          <article className='assignmentsAssessmentCard assignmentsAssessmentCardWide'>
                            <span>Prompt-by-prompt findings</span>
                            <div className='assignmentsAssessmentList'>
                              {selectedAssignmentAssessment.promptFindings.map((item, index) => (
                                <div key={`${item.promptLabel || item.promptNumber}-${index}`}>
                                  <strong>{item.promptLabel || `Prompt ${index + 1}`} • {renderAssessmentScore(item.score, 5)}</strong>
                                  <div className='assignmentsAssessmentMeter' aria-hidden='true'>
                                    <div style={{ width: `${getAssessmentScorePercent(item.score, 5)}%` }} />
                                  </div>
                                  <p>{item.responseSummary || item.masterySignal}</p>
                                  {item.coachingMove && <small>Coaching move: {item.coachingMove}</small>}
                                </div>
                              ))}
                            </div>
                          </article>
                        )}

                        {(normalizeAssessmentList(selectedAssignmentAssessment.teachingPlan?.immediateActions).length > 0
                          || normalizeAssessmentList(selectedAssignmentAssessment.teachingPlan?.nextAssignmentAdjustments).length > 0
                          || normalizeAssessmentList(selectedAssignmentAssessment.teachingPlan?.futureReadyNextSteps).length > 0) && (
                          <article className='assignmentsAssessmentCard assignmentsAssessmentCardWide'>
                            <span>Teaching plan</span>
                            <div className='assignmentsAssessmentBulletGrid'>
                              {normalizeAssessmentList(selectedAssignmentAssessment.teachingPlan?.immediateActions).length > 0 && (
                                <div>
                                  <strong>Immediate instructor actions</strong>
                                  {selectedAssignmentAssessment.teachingPlan.immediateActions.map((item, index) => <p key={`action-${index}`}>{item}</p>)}
                                </div>
                              )}
                              {normalizeAssessmentList(selectedAssignmentAssessment.teachingPlan?.nextAssignmentAdjustments).length > 0 && (
                                <div>
                                  <strong>Next assignment adjustments</strong>
                                  {selectedAssignmentAssessment.teachingPlan.nextAssignmentAdjustments.map((item, index) => <p key={`adjustment-${index}`}>{item}</p>)}
                                </div>
                              )}
                              {normalizeAssessmentList(selectedAssignmentAssessment.teachingPlan?.futureReadyNextSteps).length > 0 && (
                                <div>
                                  <strong>Future-ready next steps</strong>
                                  {selectedAssignmentAssessment.teachingPlan.futureReadyNextSteps.map((item, index) => <p key={`future-${index}`}>{item}</p>)}
                                </div>
                              )}
                            </div>
                          </article>
                        )}

                        {(normalizeAssessmentList(selectedAssignmentAssessment.studentFeedback?.celebration).length > 0
                          || normalizeAssessmentList(selectedAssignmentAssessment.studentFeedback?.improve).length > 0
                          || normalizeText(selectedAssignmentAssessment.studentFeedback?.nextChallenge)) && (
                          <article className='assignmentsAssessmentCard assignmentsAssessmentCardWide'>
                            <span>Student-facing feedback</span>
                            <div className='assignmentsAssessmentBulletGrid'>
                              {normalizeAssessmentList(selectedAssignmentAssessment.studentFeedback?.celebration).length > 0 && (
                                <div>
                                  <strong>Celebrate</strong>
                                  {selectedAssignmentAssessment.studentFeedback.celebration.map((item, index) => <p key={`celebrate-${index}`}>{item}</p>)}
                                </div>
                              )}
                              {normalizeAssessmentList(selectedAssignmentAssessment.studentFeedback?.improve).length > 0 && (
                                <div>
                                  <strong>Improve</strong>
                                  {selectedAssignmentAssessment.studentFeedback.improve.map((item, index) => <p key={`improve-${index}`}>{item}</p>)}
                                </div>
                              )}
                              {normalizeText(selectedAssignmentAssessment.studentFeedback?.nextChallenge) && (
                                <div>
                                  <strong>Next challenge</strong>
                                  <p>{selectedAssignmentAssessment.studentFeedback.nextChallenge}</p>
                                </div>
                              )}
                            </div>
                          </article>
                        )}
                      </div>
                    </div>

                    {Array.isArray(selectedAssignmentReview.assignment.promptItems) && selectedAssignmentReview.assignment.promptItems.length > 0 && (
                      <div className='assignmentsSubmissionDetailGroup'>
                        {selectedAssignmentReview.assignment.promptItems.map((prompt, index) => {
                          const answerText = selectedAssignmentReview.submission.responseItems?.[index] || getSubmissionResponseItems(selectedAssignmentReview.submission)[index] || ''
                          const answerHtml = getSubmissionResponseHtmlItems(selectedAssignmentReview.submission)[index] || ''
                          const promptHtml = typeof prompt === 'string' ? prompt : (prompt?.html || prompt?.text || '')
                          const hasAnswer = normalizeText(answerText) || normalizeText(answerHtml)

                          return (
                            <div className='assignmentsSubmissionQuestionBlock' key={`${selectedAssignmentReview.submission.id}-response-${index}`}>
                              <div
                                className='assignmentRichText assignmentPromptViewItem'
                                dangerouslySetInnerHTML={{
                                  __html: `<span>Question ${index + 1}</span>${sanitizeRichTextHtml(promptHtml)}`
                                }}
                              />
                              <div className='assignmentsSubmissionAnswer'>
                                <span>Student response</span>
                                {hasAnswer ? (
                                  answerHtml ? (
                                    <div className='assignmentRichText' dangerouslySetInnerHTML={{ __html: answerHtml }} />
                                  ) : (
                                    <p>{answerText}</p>
                                  )
                                ) : (
                                  <p>No response was captured for this question.</p>
                                )}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}

                    {getSubmissionLinks(selectedAssignmentReview.submission).length > 0 && (
                      <div className='studentProfileLinkRow'>
                        {getSubmissionLinks(selectedAssignmentReview.submission).map(([label, value]) => (
                          <a key={`${selectedAssignmentReview.submission.id}-${label}`} href={value} target='_blank' rel='noreferrer'>
                            {label}
                          </a>
                        ))}
                      </div>
                    )}
                  </article>
                </div>
              ) : (
                <div className='studentProfileReportLauncher'>
                  {assignmentReviewReports.map((item) => (
                    <button
                      key={item.submission.id}
                      type='button'
                      className='studentProfileReportTile'
                      onClick={() => setSelectedAssignmentReviewId(item.submission.id)}
                    >
                      <span className='studentProfileReportDate'>{formatFireflyShortDate(item.submission.updatedAt || item.submission.createdAt || item.submission.submittedAt)}</span>
                      <span className='studentProfileReportBody'>
                        <strong>{item.assignment.title || 'Assignment review'}</strong>
                        <small>{formatProfileDate(item.submission.updatedAt || item.submission.createdAt || item.submission.submittedAt)}</small>
                      </span>
                      <span className='studentProfileReportMeta'>
                        <span>{item.assignment.courseName || student.course || 'Course'}</span>
                        <span>{item.submission.reviewStatus || item.submission.status || 'Reviewed'}</span>
                        <span>{item.assessment?.assessment?.summary?.confidence || 'Confidence signal'}</span>
                      </span>
                      <span className='studentProfileReportTopics'>
                        {(normalizeAssessmentList(item.assessment?.assessment?.rubric).length
                          ? item.assessment.assessment.rubric.slice(0, 3).map((rubricItem) => rubricItem.label)
                          : ['Learner review']).map((label) => (
                          <span key={`${item.submission.id}-${label}`}>{label}</span>
                        ))}
                      </span>
                      <span className='studentProfileReportAction'>Open learner review</span>
                    </button>
                  ))}
                </div>
              )
            )}
          </section>
        )}

        {activeTab === 'firefly' && (
          <section className='studentProfileFirefly'>
            <div className='studentProfileFireflyTop'>
              <div>
                <span className='studentProfileEyebrow'>Firefly intelligence</span>
                <h3>Learning reports for {student.name}</h3>
                <p>Read the overall learner profile first, then open the individual sessions that need action.</p>
              </div>

              <div className='studentProfileFireflyTabs'>
                <button
                  type='button'
                  className={fireflyView === 'general' ? 'active' : ''}
                  onClick={() => setFireflyView('general')}
                >
                  General report
                </button>
                <button
                  type='button'
                  className={fireflyView === 'sessions' ? 'active' : ''}
                  onClick={() => setFireflyView('sessions')}
                >
                  Individual reports
                  {unreadFireflyCount > 0 && <span>{unreadFireflyCount}</span>}
                </button>
                <button
                  type='button'
                  className={fireflyView === 'topics' ? 'active' : ''}
                  onClick={() => setFireflyView('topics')}
                >
                  Topic intelligence
                </button>
              </div>
            </div>

            {!fireflyReports.length && !fireflyProfile ? (
              <div className='studentProfileEmpty'>
                <h4>No Firefly reports yet</h4>
                <p>Once this learner starts using Ubunifu Firefly, the teaching signals will appear here.</p>
              </div>
            ) : (
              <>
                {fireflyView === 'general' && (
                  <>
                    <div className='instructorFireflyStudentHero instructorFireflyFullHero'>
                      <div>
                        <span className='instructorCoursesEyebrow'>General report</span>
                        <h5>{fireflyProfileView?.headline || 'How Firefly currently understands this learner'}</h5>
                        <p>{fireflyProfileView?.learnerSnapshot || fireflyProfile?.summary || 'Firefly is collecting patterns from each session to build a richer learner profile.'}</p>
                      </div>

                      <div className='instructorFireflyStats instructorFireflyMetricGrid'>
                        <div className='instructorFireflyMetricCard instructorFireflyMetricSessions'>
                          <span>Reviewed sessions</span>
                          <strong>{fireflyProfileView?.sourceCount || fireflyReports.length}</strong>
                        </div>
                        <div className='instructorFireflyMetricCard instructorFireflyMetricEvidence'>
                          <span>Evidence strength</span>
                          <strong>{fireflyProfileView?.evidenceStrength || 'Live'}</strong>
                        </div>
                        <div className='instructorFireflyMetricCard instructorFireflyMetricReadiness'>
                          <span>Readiness band</span>
                          <strong>{fireflyProfileView?.readinessBand || 'Building profile'}</strong>
                        </div>
                        <div className='instructorFireflyMetricCard instructorFireflyMetricAnalysis'>
                          <span>Last analysis</span>
                          <strong>{fireflyProfileView?.lastAnalyzedLabel || fireflyProfileView?.updatedLabel || 'Live profile'}</strong>
                        </div>
                      </div>
                    </div>

                    {fireflyProfileView?.evidenceSummary && (
                      <div className='instructorFireflySummaryCard'>
                        <span className='instructorCoursesEyebrow'>Profile freshness</span>
                        <p>{fireflyProfileView.evidenceSummary}</p>
                      </div>
                    )}

                    {fireflyProfileView?.fireflyRecommendation?.shouldInviteLiveSession && (
                      <div className='instructorFireflySummaryCard instructorFireflySummaryCardAccent'>
                        <span className='instructorCoursesEyebrow'>Firefly support recommendation</span>
                        <h6>{fireflyProfileView.fireflyRecommendation.studentSummary || 'Firefly is recommending another guided live support session based on the current tutoring signals.'}</h6>
                        <div className='instructorFireflyChipGroup'>
                          {fireflyProfileView.fireflyRecommendation.focusConcepts.map((item) => (
                            <span key={`firefly-focus-${item.label}`} className='instructorFireflyChip instructorFireflyChipWarn'>{item.label}</span>
                          ))}
                        </div>
                        {fireflyProfileView.fireflyRecommendation.outreachReason && (
                          <p>{fireflyProfileView.fireflyRecommendation.outreachReason}</p>
                        )}
                      </div>
                    )}

                    <div className='instructorFireflyInsightGrid'>
                      <article className='instructorFireflyChartPanel instructorFireflyChartPanelWide'>
                        <div className='instructorFireflyChartHead'>
                          <div>
                            <span className='instructorCoursesEyebrow'>Trend</span>
                            <h6>Confidence across sessions</h6>
                          </div>
                        </div>
                        <FireflyLineChart reports={fireflyReports} />
                      </article>

                      <article className='instructorFireflyChartPanel'>
                        <div className='instructorFireflyChartHead'>
                          <div>
                            <span className='instructorCoursesEyebrow'>Topics</span>
                            <h6>Most visited concepts</h6>
                          </div>
                        </div>
                        <FireflyBarChart items={fireflyAnalytics.topTopics} emptyLabel='No topics yet' />
                      </article>

                      <article className='instructorFireflyChartPanel'>
                        <div className='instructorFireflyChartHead'>
                          <div>
                            <span className='instructorCoursesEyebrow'>Teaching</span>
                            <h6>Methods that appeared most</h6>
                          </div>
                        </div>
                        <FireflyBarChart items={fireflyAnalytics.topTeaching} emptyLabel='No method signal yet' />
                      </article>

                      <article className='instructorFireflyChartPanel'>
                        <div className='instructorFireflyChartHead'>
                          <div>
                            <span className='instructorCoursesEyebrow'>Balance</span>
                            <h6>Strengths vs gaps</h6>
                          </div>
                        </div>
                        <FireflyBalanceChart strengths={fireflyAnalytics.strengths} struggles={fireflyAnalytics.struggles} />
                      </article>
                    </div>

                    {fireflyProfileView ? (
                      <section className='studentProfilePortalCard studentProfileFireflyProfileCard'>
                        <div className='studentProfilePortalHead'>
                          <div>
                            <span className='studentProfileEyebrow'>Generated tutoring profile</span>
                            <h4>{fireflyProfileView.headline}</h4>
                            <p>{fireflyProfileView.futureOfLearningLens || 'A daily-updating tutoring profile built from all Firefly session evidence.'}</p>
                          </div>
                        </div>

                        <div className='studentProfilePortalMetrics'>
                          <div>
                            <span>Confidence signal</span>
                            <strong>{fireflyProfileView.confidence}</strong>
                          </div>
                          <div>
                            <span>Evidence strength</span>
                            <strong>{fireflyProfileView.evidenceStrength}</strong>
                          </div>
                          <div>
                            <span>Readiness</span>
                            <strong>{fireflyProfileView.readinessBand}</strong>
                          </div>
                          <div>
                            <span>Profile freshness</span>
                            <strong>{fireflyProfileView.lastAnalyzedLabel || fireflyProfileView.updatedLabel}</strong>
                          </div>
                        </div>

                        <div className='studentProfilePortalTwoCol'>
                          <div>
                            <h5>What changed since last update</h5>
                            <p>{fireflyProfileView.changeSinceLastUpdate.summary || 'The next rebuild will explain what improved, what declined, and what emerged as a new tutoring signal.'}</p>
                            <div className='studentProfileChipCloud'>
                              {fireflyProfileView.changeSinceLastUpdate.newStrengths.map((item) => <span key={`firefly-strength-${item}`}>{item}</span>)}
                              {fireflyProfileView.changeSinceLastUpdate.improvedSignals.map((item) => <span key={`firefly-improved-${item}`}>{item}</span>)}
                              {fireflyProfileView.changeSinceLastUpdate.newRisks.map((item) => <span key={`firefly-risk-${item}`}>{item}</span>)}
                              {fireflyProfileView.changeSinceLastUpdate.declinedSignals.map((item) => <span key={`firefly-declined-${item}`}>{item}</span>)}
                            </div>
                          </div>

                          <div>
                            <h5>Intervention priority</h5>
                            <div className='studentProfilePriorityStack'>
                              {(fireflyProfileView.interventionPriorities.length
                                ? fireflyProfileView.interventionPriorities
                                : [{ rank: 1, label: 'No priority locked in yet', whyItMatters: 'The profile needs more repeated Firefly evidence before ranking interventions.', firstMove: '' }]).map((item) => (
                                <div key={`firefly-priority-${item.rank}-${item.label}`} className='studentProfilePriorityCard'>
                                  <strong>{item.rank}. {item.label}</strong>
                                  {item.whyItMatters ? <p>{item.whyItMatters}</p> : null}
                                  {item.firstMove ? <small>{item.firstMove}</small> : null}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div className='studentProfilePortalTwoCol'>
                          <div>
                            <h5>Learning mode fit</h5>
                            <div className='studentProfileFitBars'>
                              {(fireflyProfileView.learningModes.length
                                ? fireflyProfileView.learningModes
                                : [{ mode: 'Still building signal', fit: 'low', score: 12, evidence: 'The profile needs more tutoring evidence before ranking delivery modes.' }]).map((item) => (
                                <div key={`firefly-fit-${item.mode}`} className='studentProfileFitRow'>
                                  <div className='studentProfileFitTop'>
                                    <span>{item.mode}</span>
                                    <small>{item.fit}</small>
                                  </div>
                                  <i style={{ width: `${Math.max(8, item.score)}%` }} />
                                  {item.evidence ? <p>{item.evidence}</p> : null}
                                </div>
                              ))}
                            </div>
                          </div>

                          <div>
                            <h5>Teacher moves by timeframe</h5>
                            <div className='studentProfileMoveTimeline'>
                              <div>
                                <span>Next lesson</span>
                                <p>{fireflyProfileView.nextLessonMoves.join(', ') || 'No next-lesson move surfaced yet.'}</p>
                              </div>
                              <div>
                                <span>This week</span>
                                <p>{fireflyProfileView.thisWeekMoves.join(', ') || 'No weekly move surfaced yet.'}</p>
                              </div>
                              <div>
                                <span>Next assessment cycle</span>
                                <p>{fireflyProfileView.nextAssessmentCycleMoves.join(', ') || 'No assessment-cycle move surfaced yet.'}</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className='studentProfilePortalTwoCol'>
                          <div>
                            <h5>How Firefly helps this learner learn best</h5>
                            <div className='studentProfileSignalDetail'>
                              <strong>Preferred teaching modes</strong>
                              <p>{fireflyProfileView.sessionIntelligence.preferredTeachingModes.join(', ') || 'No preferred mode pattern has been locked in yet.'}</p>
                            </div>
                            <div className='studentProfileSignalDetail'>
                              <strong>Explanation formats that work</strong>
                              <p>{fireflyProfileView.sessionIntelligence.explanationFormatsThatWork.join(', ') || 'No explanation format pattern has been locked in yet.'}</p>
                            </div>
                            <div className='studentProfileSignalDetail'>
                              <strong>Engagement pattern</strong>
                              <p>{fireflyProfileView.sessionIntelligence.engagementPattern || fireflyProfileView.sessionIntelligence.pacePattern || 'The tutoring rhythm is still being mapped.'}</p>
                            </div>
                          </div>

                          <div>
                            <h5>Concept mastery map</h5>
                            <div className='studentProfileSignalDetail'>
                              <strong>Strongest themes</strong>
                              <p>{fireflyProfileView.conceptMasteryMap.strongestThemes.map((item) => item.label).join(', ') || 'No strongest theme locked in yet.'}</p>
                            </div>
                            <div className='studentProfileSignalDetail'>
                              <strong>Fragile themes</strong>
                              <p>{fireflyProfileView.conceptMasteryMap.fragileThemes.map((item) => item.label).join(', ') || 'No fragile theme surfaced yet.'}</p>
                            </div>
                            <div className='studentProfileSignalDetail'>
                              <strong>Misconception threads</strong>
                              <p>{fireflyProfileView.conceptMasteryMap.misconceptionThreads.map((item) => item.label).join(', ') || 'No repeating misconception thread surfaced yet.'}</p>
                            </div>
                          </div>
                        </div>

                        <div className='studentProfilePortalTwoCol'>
                          <div>
                            <h5>Student-facing note</h5>
                            <p>{fireflyProfileView.studentFacing.encouragement || 'The student-facing message will appear here as soon as the tutoring profile becomes more stable.'}</p>
                            <div className='studentProfileSignalDetail'>
                              <strong>What is going well</strong>
                              <p>{fireflyProfileView.studentFacing.whatIsGoingWell.join(', ') || 'No student-facing win has been surfaced yet.'}</p>
                            </div>
                            <div className='studentProfileSignalDetail'>
                              <strong>Next focus to communicate</strong>
                              <p>{fireflyProfileView.studentFacing.focusNext.join(', ') || 'No student-facing focus has been surfaced yet.'}</p>
                            </div>
                            {(fireflyProfileView.studentFacing.whyThisMatters || fireflyProfileView.studentFacing.reflectionPrompt) && (
                              <div className='studentProfileSignalDetail'>
                                {fireflyProfileView.studentFacing.whyThisMatters && <p><strong>Why this matters:</strong> {fireflyProfileView.studentFacing.whyThisMatters}</p>}
                                {fireflyProfileView.studentFacing.reflectionPrompt && <p><strong>Reflection prompt:</strong> {fireflyProfileView.studentFacing.reflectionPrompt}</p>}
                              </div>
                            )}
                          </div>

                          <div>
                            <h5>Misconception recurrence</h5>
                            <div className='studentProfileDiagnosisList'>
                              {(fireflyProfileView.misconceptionRecurrence.length
                                ? fireflyProfileView.misconceptionRecurrence
                                : fireflyProfileView.conceptMasteryMap.misconceptionThreads.length
                                  ? fireflyProfileView.conceptMasteryMap.misconceptionThreads
                                  : [{ label: 'No recurring misconception yet', evidence: 'Repeated misconceptions will appear here as soon as Firefly sees the same pattern often enough.' }]).map((item, index) => (
                                <article key={`firefly-diagnosis-${index}`} className='studentProfileDiagnosisCard'>
                                  <div className='studentProfileDiagnosisBlock'>
                                    <span>Pattern</span>
                                    <p>{item.label}</p>
                                  </div>
                                  {item.evidence ? (
                                    <div className='studentProfileDiagnosisBlock'>
                                      <span>Evidence</span>
                                      <p>{item.evidence}</p>
                                    </div>
                                  ) : null}
                                  {item.action ? (
                                    <div className='studentProfileDiagnosisBlock'>
                                      <span>Recommended move</span>
                                      <p>{item.action}</p>
                                    </div>
                                  ) : null}
                                </article>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div className='studentProfilePortalTwoCol'>
                          <div>
                            <h5>Firefly tutoring playbook</h5>
                            <div className='studentProfileMoveTimeline'>
                              <div>
                                <span>Opening moves</span>
                                <p>{fireflyProfileView.tutoringPlaybook.openingMoves.join(', ') || 'No opening move has been surfaced yet.'}</p>
                              </div>
                              <div>
                                <span>In-session moves</span>
                                <p>{fireflyProfileView.tutoringPlaybook.inSessionMoves.join(', ') || 'No in-session move has been surfaced yet.'}</p>
                              </div>
                              <div>
                                <span>Checks for understanding</span>
                                <p>{fireflyProfileView.tutoringPlaybook.checksForUnderstanding.join(', ') || 'No checks-for-understanding move has been surfaced yet.'}</p>
                              </div>
                              <div>
                                <span>Board and visual strategies</span>
                                <p>{fireflyProfileView.tutoringPlaybook.boardAndVisualStrategies.join(', ') || 'No board or visual strategy has been surfaced yet.'}</p>
                              </div>
                            </div>
                          </div>

                          <div>
                            <h5>Support focus next</h5>
                            <div className='studentProfilePriorityStack'>
                              {(fireflyProfileView.interventionPriorities.length
                                ? fireflyProfileView.interventionPriorities
                                : [{ rank: 1, label: 'No priority locked in yet', whyItMatters: 'The profile needs more repeated Firefly evidence before ranking interventions.', firstMove: '' }]).map((item) => (
                                <div key={`firefly-priority-${item.rank}-${item.label}`} className='studentProfilePriorityCard'>
                                  <strong>{item.rank}. {item.label}</strong>
                                  {item.whyItMatters ? <p>{item.whyItMatters}</p> : null}
                                  {item.firstMove ? <small>{item.firstMove}</small> : null}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </section>
                    ) : fireflyProfile && (
                      <section className='studentProfilePortalCard'>
                        <div className='studentProfilePortalHead'>
                          <div>
                            <span className='studentProfileEyebrow'>Learner profile</span>
                            <h4>How Firefly currently understands {student.name}</h4>
                          </div>
                        </div>

                        <div className='studentProfilePortalMetrics'>
                          <div>
                            <span>Pace</span>
                            <strong>{fireflyProfile.learningPace || 'Still learning pace'}</strong>
                          </div>
                          <div>
                            <span>Confidence trend</span>
                            <strong>{fireflyProfile.confidenceTrend || 'Building signal'}</strong>
                          </div>
                          <div>
                            <span>Preferred modes</span>
                            <strong>{fireflyProfile.preferredTeachingModes?.length ? fireflyProfile.preferredTeachingModes.join(', ') : 'Detecting best mode'}</strong>
                          </div>
                          <div>
                            <span>Topics seen</span>
                            <strong>{fireflyProfile.topicsSeen?.length || fireflyAnalytics.totalTopics || 0}</strong>
                          </div>
                        </div>

                        {fireflyProfile.summary ? (
                          <div className='studentProfilePortalSummary'>
                            <h5>Profile summary</h5>
                            <p>{fireflyProfile.summary}</p>
                          </div>
                        ) : null}

                        <div className='studentProfilePortalTwoCol'>
                          <div>
                            <h5>Strengths</h5>
                            <ul>
                              {(fireflyProfile.strengths?.length ? fireflyProfile.strengths : ['Firefly is still building a stronger strengths profile over the next few sessions.']).map((item, index) => (
                                <li key={`profile-strength-${index}`}>{item}</li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <h5>Challenges</h5>
                            <ul>
                              {(fireflyProfile.struggles?.length ? fireflyProfile.struggles : ['No major challenge pattern has been locked in yet.']).map((item, index) => (
                                <li key={`profile-struggle-${index}`}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </section>
                    )}
                  </>
                )}

                {fireflyView === 'sessions' && (
                  <>
                    {selectedReportId && selectedReport ? (
                      <div className='studentProfileFireflyReader'>
                        <button type='button' className='studentProfileBackButton' onClick={() => setSelectedReportId('')}>
                          <FontAwesomeIcon icon={faArrowLeft} />
                          Back to report list
                        </button>

                        <article className='instructorFireflyReport'>
                          <div className='instructorFireflyReportTop'>
                            <div>
                              <span className='instructorCoursesEyebrow'>Individual report</span>
                              <h5>{selectedReport.sessionFocus || 'General Firefly lesson'}</h5>
                              <p>{formatFireflyDate(selectedReport.endedAtMs || selectedReport.startedAtMs)}</p>
                            </div>
                            <strong>{selectedReport.confidenceTrend || 'Steady confidence'}</strong>
                          </div>

                          <div className='instructorFireflyReportMeta'>
                            <span>{formatFireflyDuration(selectedReport.durationSeconds)}</span>
                            <span>{selectedReport.learningPace || 'Steady pace'}</span>
                            <span>{selectedReport.topicsCovered.length} topic{selectedReport.topicsCovered.length === 1 ? '' : 's'}</span>
                          </div>

                          <div className='instructorFireflyReportGrid'>
                            <div>
                              <h6>Summary</h6>
                              <p>{selectedReport.summary || 'No summary was written for this session.'}</p>
                            </div>
                            <div>
                              <h6>Topics covered</h6>
                              <p>{selectedReport.topicsCovered.join(', ') || 'No topic list was captured.'}</p>
                            </div>
                            <div>
                              <h6>Strengths</h6>
                              <p>{selectedReport.strengths.join(', ') || 'No clear strengths captured in this session.'}</p>
                            </div>
                            <div>
                              <h6>Challenges</h6>
                              <p>{selectedReport.struggles.join(', ') || 'No major challenge captured in this session.'}</p>
                            </div>
                            <div>
                              <h6>Teaching methods</h6>
                              <p>{selectedReport.teachingMethodsUsed.join(', ') || 'Adaptive tutoring'}</p>
                            </div>
                            <div>
                              <h6>Next steps</h6>
                              <p>{selectedReport.recommendedNextSteps.join(', ') || 'Start the next support moment from one focused issue.'}</p>
                            </div>
                          </div>

                          {selectedReport.diagnoses.length > 0 && (
                            <div className='instructorFireflyDiagnosis'>
                              <h6>Fine-grained diagnosis</h6>
                              {selectedReport.diagnoses.map((item, index) => (
                                <div key={`${selectedReport.id}-diagnosis-${index}`}>
                                  <strong>{item.topic || 'General concept'}</strong>
                                  {item.subtopic ? <small>{item.subtopic}</small> : null}
                                  <p>{item.misconception || item.evidence || item.bestIntervention}</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </article>
                      </div>
                    ) : (
                      <div className='studentProfileReportLauncher'>
                        {fireflyReports.map((report) => {
                          const readId = buildFireflyReadId(instructorId, studentId, report.id)
                          const isUnread = !fireflyReadMap[readId]

                          return (
                            <button
                              key={report.id}
                              type='button'
                              className='studentProfileReportTile'
                              onClick={() => setSelectedReportId(report.id)}
                            >
                              <span className='studentProfileReportDate'>{formatFireflyShortDate(report.endedAtMs || report.startedAtMs)}</span>
                              <span className='studentProfileReportBody'>
                                <strong>{report.sessionFocus || 'General Firefly lesson'}</strong>
                                <small>{formatFireflyDate(report.endedAtMs || report.startedAtMs)}</small>
                              </span>
                              <span className='studentProfileReportMeta'>
                                <span>{formatFireflyDuration(report.durationSeconds)}</span>
                                <span>{report.learningPace || 'Steady pace'}</span>
                                <span>{report.topicsCovered.length || 0} topics</span>
                              </span>
                              <span className='studentProfileReportTopics'>
                                {(report.topicsCovered.length ? report.topicsCovered.slice(0, 3) : ['General lesson']).map((topic) => (
                                  <span key={`${report.id}-${topic}`}>{topic}</span>
                                ))}
                              </span>
                              <span className='studentProfileReportAction'>
                                Open report
                                {isUnread && <b>New</b>}
                              </span>
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </>
                )}

                {fireflyView === 'topics' && (
                  <section className='studentProfilePortalCard'>
                    <div className='studentProfilePortalHead'>
                      <div>
                        <span className='studentProfileEyebrow'>Topic intelligence</span>
                        <h4>What Firefly should keep tracking</h4>
                        <p>Topic frequency, misconceptions, and interventions collected across this learner’s sessions.</p>
                      </div>
                    </div>

                    <div className='studentProfileTopicGrid'>
                      {(fireflyAnalytics.topTopics.length ? fireflyAnalytics.topTopics : [{ label: 'No topics yet', value: 0 }]).map((topic) => (
                        <article key={topic.label} className='studentProfileTopicCard'>
                          <div>
                            <span>Concept</span>
                            <h5>{topic.label}</h5>
                          </div>
                          <strong>{topic.value}</strong>
                        </article>
                      ))}
                    </div>

                    {fireflyDiagnosisMap.length > 0 ? (
                      <div className='studentProfileDiagnosisList'>
                        {fireflyDiagnosisMap.map((item, index) => (
                          <article key={`diagnosis-${index}`} className='studentProfileDiagnosisCard'>
                            <div className='studentProfileDiagnosisLine'>
                              <span>Topic</span>
                              <strong>{item.topic || 'General concept'}</strong>
                            </div>
                            {item.subtopic ? (
                              <div className='studentProfileDiagnosisLine'>
                                <span>Subtopic</span>
                                <strong>{item.subtopic}</strong>
                              </div>
                            ) : null}
                            {item.misconception ? (
                              <div className='studentProfileDiagnosisBlock'>
                                <span>Misconception</span>
                                <p>{item.misconception}</p>
                              </div>
                            ) : null}
                            {item.bestIntervention ? (
                              <div className='studentProfileDiagnosisBlock'>
                                <span>Best intervention</span>
                                <p>{item.bestIntervention}</p>
                              </div>
                            ) : null}
                          </article>
                        ))}
                      </div>
                    ) : (
                      <div className='studentProfileEmpty'>
                        <h4>No diagnosis map yet</h4>
                        <p>After a few focused sessions, Firefly will start showing topic-level misconceptions and interventions here.</p>
                      </div>
                    )}
                  </section>
                )}
              </>
            )}
          </section>
        )}
      </section>
    </div>
  )
}

export default StudentProfileComp
